import React, { createElement, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { View, Text, Pressable, TextInput } from '../lib/react-native-compat';
import { loadFieldTicketReviews, fieldTicketPacketStore, fieldTicketPacketList, fieldTicketPacketGet } from '../db/company';
import { showToast } from '../helpers/toast';
import { OutlineButton } from './procurementFilters';
import {
    DOC_SHIFTS, cellKey, splitCell, sortCells, newDocId, docRows, removedDocRows, capHistory,
    docName, docFileName, docLocked, reviewsByCell,
    suggestBackup, autoBackup, cellsWithoutBackup, DOC_ACCEPT, MAX_DOC_BYTES,
    testSigners, testDocId,
    PART_TITLE, PART_SHORT, docParts, partCells, partSigners, partDocId, partEnvelopes, docLockedAny, docStage, isSummaryDoc,
    isSupplementDoc, onlyNames,
    SRC_FIELDS, normalizeSrc, compactSrc, srcIsCustom, srcSummary, packetManifest, fmtBytes, readinessChecks,
} from './billingTicketDocs';
import { weekTracker } from './billingWeekTracker';

// Ticket Map — the Time Card Approval tab's modal for field ticket documents.
//
// Left: every work day × shift, tinted by the document that covers it. Right:
// the selected document, built in the order the work actually happens:
//   1. COVERS  — click day/shift cells on the map to put them on the ticket
//   2. BACKUP  — the SharePoint scans that support it (the day's scans attach
//                on their own; the rest are suggested by date)
//   3. SOURCES — where each day's hours, day/night split, classifications and
//                equipment will come from (reviewed time card, attendance
//                roster, HeavyJob, Staff List workbook), the choices that
//                change that order (stored on the document as `src`), a note
//                saying why, and the readiness checks over all of it
//   4-6. SEND  — three documents to DocuSign, each its own envelope: the day
//                crew's tickets (cover + days + their backup), the night
//                crew's, and the week summary over both. Each goes to the
//                signers on the job's map whose role covers it, so the day
//                rep signs their week without waiting on the night rep.
// The AI check and the Strike-side approval that used to sit between the
// backup and the sends were taken off this screen; their helpers still live in
// billingTicketDocs.js. A send is gated on nothing now but having somebody to
// send it to. The document saves as you go — there is no separate save — and
// freezes once its envelope is out. A ticket signed on paper takes an uploaded signed copy
// instead of an envelope and freezes the same way.
//
// Every packet built here — previewed, downloaded, sent, test-sent — is filed
// in Azure Blob Storage with a manifest of its inputs (fieldTicketPacket.js),
// and listed back under the ticket (and job-wide when no ticket is picked):
// what went to the client, who built it, what it was built from, and whether
// a later build still matches what was sent.
//
// A SUPPLEMENT (kind 'supplement') is a ticket for people a signed ticket
// missed: it names who is on it and may sit on days another ticket owns —
// the map shows it as a lettered badge beside the day's owner. Its packet
// prints the named people alone (billingTicketDocs narrowTicket), so the
// client never signs the same hours twice.
//
// Pure render helpers inside, no nested components: the parent re-renders on
// every stored-row change and nested component identities would remount.

const PALETTE = ['#2563eb', '#0d9488', '#7c3aed', '#c2410c', '#be185d', '#4d7c0f', '#0369a1', '#a16207'];
// What the packet buttons are doing, for the progress line under them.
const BUSY_LABEL = {
    preview: 'Building the preview', download: 'Building the PDF', send: 'Preparing the envelope',
};
// What each of the three sends carries, said once under its heading.
const PART_BLURB = {
    day: 'the day crew’s week summary, every day ticket on this document, then the backup pages the AI matched to those days (upright).',
    night: 'the night crew’s week summary, every night ticket on this document, then the backup pages the AI matched to those nights (upright).',
    summary: 'the week summaries only — all shifts first, then each crew. No day tickets and no backup: those go with the crew sends.',
};
const START_CONCURRENCY = 3;

const md = (d) => moment(d, 'YYYY-MM-DD').format('M/D');
const fmtDay = (d) => moment(d, 'YYYY-MM-DD').format('ddd M/D/YY');
const card = { borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, backgroundColor: '#fff' };
const sectionLabel = { fontSize: 10, fontWeight: '700', letterSpacing: 0.6, color: '#94a3b8' };
const btnDark = (off) => ({
    backgroundColor: '#0f172a', borderRadius: 4, paddingTop: 5, paddingBottom: 5, paddingLeft: 12, paddingRight: 12,
    cursor: off ? 'default' : 'pointer', opacity: off ? 0.45 : 1,
});

const TicketMap = ({
    jobKey, versionLocked, userName, userEmail, settings,
    days,          // [{ d, hasDetail }] newest first
    docs,          // parseDocs(userRows)
    sigByDoc,      // envelopesByDoc(envelopes)
    envelopes,     // the job's envelope rows — the week tracker reads the invoice approvals off them
    legacyStatus,  // (d, sh) => { label, dot } | null — per-day sheets from before documents
    files, filesLoading, fileUrl, fileDate,
    focus,         // { cells: [...] } to start on, or { docId }; with `kind` ('day' |
                   // 'combined' | 'summary') it is the Reconciliation view's hand-off
    dataKey,       // changes when shift overrides / roster / classifications change
    onClose, saveRows,
    cellTickets,   // async (cells) => { [cell]: shiftTicket }
    cellPayload,   // async (cell, docCells) => { ticket, context }
    buildPacket,   // async (doc, byCell, onStep, { part }) => { name, bytes }
    sendPacket,    // async (doc, form, packet) => void — form.signers is the recipient list
    recordRecon,   // async (doc, part) => { inserted } — HeavyJob snapshot for the Reconciliation view
    renderEnvelope,
    uploadFile,    // async (file, date, { name?, level? }) => { id, driveId, name, path, webUrl }
    onFilesChanged, // () => void — re-sync the SharePoint listing after an upload
    daySources,    // (d, src) => what one day's ticket is built from — see the approval tab
    roster,        // { loading, error, found, file } — the attendance roster read
    sourceFiles,   // { staffList: { name, lastModified }?, roster: { name, lastModified }? }
    equipWeeks,    // { [weekEnding]: { units, at } } — reviewed weekly time cards
    importTimecard, // async (file, weekEnding) => units — parses + stores one
    sigFlow = 'client', // 'client' | 'test' — shared with the Upload dialog
    setSigFlow,
    docusignDemo,  // true when this environment's DocuSign is the demo sandbox
}) => {
    // Documents saved from here show up in `docs` only after the stored rows
    // round-trip; `local` holds the newer copy until then (higher rev wins).
    const [local, setLocal] = useState({});
    const docById = useMemo(() => {
        const m = {};
        (docs || []).forEach((d) => { m[d.id] = d; });
        Object.values(local).forEach((d) => {
            if (!m[d.id] || (d.rev || 0) >= (m[d.id].rev || 0)) m[d.id] = d;
        });
        return m;
    }, [docs, local]);
    const liveDocs = useMemo(() => Object.values(docById).filter((d) => !d.del)
        .sort((a, b) => String(a.h?.[0]?.t || '').localeCompare(String(b.h?.[0]?.t || '')) || a.id.localeCompare(b.id)), [docById]);
    // The map reads the merged view so a just-added cell tints immediately.
    // A week summary on its own sits over its days' tickets — it claims no
    // cell (isSummaryDoc); the week summaries list under the map opens it.
    const cover = useMemo(() => {
        const map = {};
        liveDocs.filter((doc) => !isSummaryDoc(doc) && !isSupplementDoc(doc)).forEach((doc) => doc.cells.forEach((k) => { if (!map[k]) map[k] = doc; }));
        return map;
    }, [liveDocs]);
    // Supplements beside each cell's owner (cell -> [doc]); they claim nothing.
    const extras = useMemo(() => {
        const map = {};
        liveDocs.filter(isSupplementDoc).forEach((doc) => doc.cells.forEach((k) => { (map[k] = map[k] || []).push(doc); }));
        return map;
    }, [liveDocs]);
    const summaryDocs = useMemo(() => liveDocs.filter(isSummaryDoc), [liveDocs]);
    // Where each week stands: daily tickets, week summary, invoice.
    const weekRows = useMemo(() => weekTracker({
        docs: liveDocs, sigByDoc, envelopes, days: (days || []).filter((x) => x.hasDetail).map((x) => x.d),
    }), [liveDocs, sigByDoc, envelopes, days]);
    const supplementDocs = useMemo(() => liveDocs.filter(isSupplementDoc), [liveDocs]);
    // A week summary or a supplement takes any day, whatever ticket holds it.
    const sharesCells = (d) => isSummaryDoc(d) || isSupplementDoc(d);
    const mapDays = useMemo(() => {
        const m = new Map((days || []).map((x) => [x.d, x]));
        liveDocs.forEach((doc) => doc.cells.forEach((k) => {
            const { d } = splitCell(k);
            if (!m.has(d)) m.set(d, { d, hasDetail: false });
        }));
        return [...m.values()].sort((a, b) => b.d.localeCompare(a.d));
    }, [days, liveDocs]);
    const colorOf = (doc) => PALETTE[Math.max(0, liveDocs.findIndex((d) => d.id === doc.id)) % PALETTE.length];
    const letterOf = (doc) => String.fromCharCode(65 + (Math.max(0, liveDocs.findIndex((d) => d.id === doc.id)) % 26));

    const [selId, setSelId] = useState(null);
    const [extraDate, setExtraDate] = useState('');
    const [editCells, setEditCells] = useState(false);
    const doc = selId ? docById[selId] : null;
    // One envelope per send (day / night / summary), plus the document's own
    // for tickets sent before the split — partEnvelopes stands that old one
    // in for all three.
    const envs = useMemo(() => (doc ? partEnvelopes(doc, sigByDoc) : {}), [doc, sigByDoc]);
    const parts = useMemo(() => (doc ? docParts(doc) : []), [doc]);
    const liveEnvs = parts.map((p) => envs[p]).filter(Boolean);
    const anyEnvLive = liveEnvs.some((e) => !['voided', 'expired', 'failed'].includes(String(e.Status || '').toLowerCase()));
    const locked = Boolean(versionLocked || (doc && docLockedAny(doc, envs)));

    const stamp = (s, n) => ({ s, u: String(userName || '').slice(0, 30), t: moment().format('YYYY-MM-DD HH:mm'), ...(n ? { n: String(n).slice(0, 160) } : {}) });
    const persist = (next) => {
        const d = { ...next, rev: (next.rev || 0) + 1, h: capHistory(next.h || []) };
        setLocal((l) => ({ ...l, [d.id]: d }));
        saveRows(d.del ? removedDocRows(d) : docRows(d));
        return d;
    };

    // A fresh, empty ticket with the day/shift picker live. It stays local
    // until something is put on it — closing an untouched one leaves nothing
    // stored behind.
    const startNew = (kind) => {
        if (versionLocked) return;
        const d = {
            id: newDocId(), cells: [], files: [], st: 'Draft', h: [stamp('Draft', kind === 'supplement' ? 'Supplement — people a signed ticket missed' : undefined)], ack: {}, rev: 0,
            ...(kind === 'supplement' ? { kind: 'supplement', only: [] } : {}),
        };
        // An earlier untouched one (nothing stored, nothing on it) goes, so
        // it doesn't sit on the map taking a letter.
        const untouched = (x) => !x.rev && !x.cells.length && !x.files.length && !x.del;
        setLocal((l) => ({ ...Object.fromEntries(Object.entries(l).filter(([, x]) => !untouched(x))), [d.id]: d }));
        setSelId(d.id);
        setEditCells(true);
    };

    // ── Opening focus: a new ticket, a cell from a day chip, or a document ──
    const focused = useRef(false);
    useEffect(() => {
        if (focused.current || !focus) return;
        focused.current = true;
        if (focus.docId && docById[focus.docId]) { setSelId(focus.docId); return; }
        if (focus.newDoc) { startNew(); return; }
        const cells = sortCells(focus.cells || []);
        if (!cells.length) return;
        if (focus.kind) { openHandoff(cells, focus.kind); return; }
        const owner = cover[cells[0]];
        if (owner) { setSelId(owner.id); return; }
        if (versionLocked) return;
        const free = cells.filter((k) => !cover[k]);
        const created = persist({ id: newDocId(), cells: free, files: [], st: 'Draft', h: [stamp('Draft')], ack: {} });
        setSelId(created.id);
        setEditCells(true);
    }, [focus]); // deliberately narrow deps

    // The Reconciliation view's hand-off: days picked there, opened as a
    // Draft to review and send from here — nothing goes to DocuSign.
    //   day / combined — the days' own ticket: open the one ticket that
    //                    already holds them all, else a new Draft on the days
    //                    no ticket holds (the rest are named, not moved)
    //   summary        — a week summary on its own over the days, whatever
    //                    tickets hold them; one already over exactly these
    //                    days opens instead
    function openHandoff(cells, kind) {
        const same = (d) => sortCells(d.cells).join() === cells.join();
        if (kind === 'summary') {
            const had = summaryDocs.find(same);
            if (had) { setSelId(had.id); showToast(`These days already have week summary ${letterOf(had)} — opened it.`, 'info'); return; }
            if (versionLocked) return;
            const created = persist({ id: newDocId(), kind: 'summary', cells, files: [], st: 'Draft', h: [stamp('Draft', 'Week summary from Reconciliation')], ack: {} });
            setSelId(created.id);
            return;
        }
        const owners = [...new Map(cells.filter((k) => cover[k]).map((k) => [cover[k].id, cover[k]])).values()];
        const free = cells.filter((k) => !cover[k]);
        if (!free.length) {
            setSelId(owners[0].id);
            showToast(owners.length === 1
                ? `${docName(cells)} is already on ticket ${letterOf(owners[0])} — opened it.`
                : `Those days are already on tickets ${owners.map(letterOf).join(', ')} — opened ${letterOf(owners[0])}.`, 'info');
            return;
        }
        if (versionLocked) return;
        const created = persist({ id: newDocId(), cells: free, files: [], st: 'Draft', h: [stamp('Draft', 'From Reconciliation')], ack: {} });
        setSelId(created.id);
        setEditCells(true);
        if (owners.length) {
            const on = owners.map((o) => `${letterOf(o)} (${docName(cells.filter((k) => cover[k]?.id === o.id))})`).join(', ');
            showToast(`Left off — already on a ticket: ${on}.`, 'warn');
        }
    }

    // ── Map clicks ──
    const clickCell = (d, sh, has) => {
        const k = cellKey(d, sh);
        const owner = cover[k];
        if (doc && editCells && !locked) {
            // A week summary or a supplement takes any day, whatever ticket holds it.
            if (owner && owner.id !== doc.id && !sharesCells(doc)) { showToast(`${md(d)} ${sh} is on ticket ${letterOf(owner)} — ${docName(owner.cells)}. For someone that ticket missed, start a supplement instead.`, 'warn'); return; }
            if (!has && !doc.cells.includes(k)) showToast(`No hours found for ${md(d)} ${sh} yet — added anyway.`, 'warn');
            const on = doc.cells.includes(k);
            const cells = on ? doc.cells.filter((x) => x !== k) : sortCells([...doc.cells, k]);
            // A day taken off and put back gets its scans pulled again.
            persist({ ...doc, cells, ...(on && doc.af ? { af: doc.af.filter((x) => x !== k) } : {}) });
            return;
        }
        if (owner) { setSelId(owner.id); setEditCells(false); return; }
        if (versionLocked) return;
        const created = persist({ id: newDocId(), cells: [k], files: [], st: 'Draft', h: [stamp('Draft')], ack: {} });
        setSelId(created.id);
        setEditCells(true);
    };

    // ── Reviews for the selected document ──
    const [reviews, setReviews] = useState({ docId: null, list: [], loading: false });
    const loadReviews = (id) => {
        if (!id) return;
        setReviews((r) => (r.docId === id ? { ...r, loading: true } : { docId: id, list: [], loading: true }));
        loadFieldTicketReviews({ job: String(jobKey), docId: id })
            .then((res) => setReviews((r) => (r.docId === id ? { docId: id, list: res?.reviews || [], loading: false, error: res?.error } : r)))
            .catch((e) => setReviews((r) => (r.docId === id ? { ...r, loading: false, error: String(e?.message || e) } : r)));
    };
    useEffect(() => { if (selId) loadReviews(selId); }, [selId]); // deliberately narrow deps
    // Reviews are no longer run or shown from here, but a review that HAS run
    // still carries the page map the packet binds backup by — which scan page
    // belongs to which day, and which way up. Read once per document, used by
    // buildPacket alone.
    const byCell = useMemo(() => (doc && reviews.docId === doc.id ? reviewsByCell(doc, reviews.list) : {}), [doc, reviews]);

    // Packet build state, declared up here because the shared clock below
    // ticks for it too.
    const [busy, setBusy] = useState('');
    // Where the build has got to. A week's packet is minutes of day pulls,
    // ticket drawing and backup binding, so the button alone reads as a hang:
    // buildPacket narrates itself through here and the line under the buttons
    // says which day (or which scan) it is on, with a clock.
    const [pkStep, setPkStep] = useState(null);
    const [pkAt, setPkAt] = useState(0);
    const startBusy = (what) => { setBusy(what); setPkStep(null); setPkAt(Date.now()); };
    const endBusy = () => { setBusy(''); setPkStep(null); setPkAt(0); };
    const pkSecs = pkAt ? Math.floor((Date.now() - pkAt) / 1000) : 0;

    // Ticks the elapsed counter while a packet builds, so a long build still
    // looks alive.
    const [, setClock] = useState(0);
    useEffect(() => {
        if (!busy) return undefined;
        const t = setInterval(() => setClock((x) => x + 1), 1000);
        return () => clearInterval(t);
    }, [busy]); // deliberately narrow deps
    // Everyone on the job's signer map whose role covers a send is on that
    // envelope by default; a person can be taken off for one send (a rep on
    // vacation, a crew the client wants signed by one person). Kept per
    // document and send, and not stored — the map stays the job's standing
    // answer.
    const [offSigners, setOffSigners] = useState({});
    const offKey = (part, r) => `${doc?.id || ''}|${part}|${String(r.email || '').toLowerCase()}`;
    const toggleSigner = (part, r) => setOffSigners((m) => ({ ...m, [offKey(part, r)]: !m[offKey(part, r)] }));
    // Test flow: the packet goes to me alone, ungated, under a key the ticket
    // doesn't own — so its envelope shows here but never drives the ticket.
    const testFlow = sigFlow === 'test';
    const testTo = useMemo(() => (doc ? testSigners(settings, doc.cells, { name: userName, email: userEmail }) : []), [settings, doc && doc.cells, userName, userEmail]); // deliberately narrow deps

    // ── Packet archive (Azure Blob Storage) ──
    // Every packet built here is kept with a manifest of its inputs
    // (fieldTicketPacket.js), so what went to the client, and what it was
    // built from, can be opened again — and a later build compared to it.
    const [packets, setPackets] = useState({ job: null, list: [], loading: false, error: '' });
    const loadPackets = () => {
        if (!jobKey) return;
        setPackets((p) => ({ ...p, job: jobKey, loading: true }));
        fieldTicketPacketList({ job: String(jobKey) })
            // The request helper hands back nothing on a failed call, and
            // "nothing" is not "no packets": an archive that cannot be read
            // says so rather than reading as empty.
            .then((res) => setPackets({
                job: jobKey,
                list: res?.packets || [],
                loading: false,
                error: !res ? 'no response from the server (is fieldTicketPacket deployed here?)' : res.error ? String(res.error) : '',
            }))
            .catch((e) => setPackets({ job: jobKey, list: [], loading: false, error: String(e?.message || e) }));
    };
    useEffect(() => { loadPackets(); }, [jobKey]); // deliberately narrow deps
    // PDF bytes -> base64, chunked: String.fromCharCode.apply blows the call
    // stack on a whole packet at once.
    const toBase64 = (bytes) => {
        const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
        const CHUNK = 0x8000;
        let bin = '';
        for (let i = 0; i < b.length; i += CHUNK) bin += String.fromCharCode.apply(null, b.subarray(i, i + CHUNK));
        return btoa(bin);
    };
    // Files one built packet. Never fatal: the packet already opened, was
    // saved, or went out — a copy that fails to file is said, not thrown.
    const archivePacket = async (d, pk, part, purpose, extra = {}) => {
        try {
            setPkStep({ msg: 'filing a copy in the packet archive', done: 0, total: 0 });
            const manifest = packetManifest(d, pk, { part, purpose, me: { name: userName, email: userEmail }, stage: docStage(d, partEnvelopes(d, sigByDoc)) || d.st, ...extra });
            const res = await fieldTicketPacketStore({
                job: String(jobKey), docId: d.id, part, purpose, name: pk.name, contentBase64: toBase64(pk.bytes), manifest,
            });
            if (res?.error) throw new Error(res.error);
            loadPackets();
            return res;
        } catch (e) {
            showToast(`The packet was built, but its archive copy failed — ${e?.message || e}`, 'warn');
            return null;
        }
    };
    // One archived packet's manifest and read link, fetched when it is
    // opened or expanded. A SAS link lives an hour; past fifty minutes it is
    // asked for again.
    const [pkOpen, setPkOpen] = useState({});
    const fetchPacket = async (id) => {
        setPkOpen((m) => ({ ...m, [id]: { ...(m[id] || {}), loading: true } }));
        try {
            const res = await fieldTicketPacketGet({ job: String(jobKey), id });
            if (res?.error) throw new Error(res.error);
            const v = { loading: false, manifest: res.manifest, url: res.url, name: res.name, at: Date.now() };
            setPkOpen((m) => ({ ...m, [id]: { ...(m[id] || {}), ...v } }));
            return v;
        } catch (e) {
            const v = { loading: false, error: String(e?.message || e) };
            setPkOpen((m) => ({ ...m, [id]: { ...(m[id] || {}), ...v } }));
            return v;
        }
    };
    const openPacket = async (p) => {
        const have = pkOpen[p.id];
        const fresh = have?.url && Date.now() - (have.at || 0) < 50 * 60 * 1000 ? have : await fetchPacket(p.id);
        if (fresh?.url) window.open(fresh.url, '_blank', 'noopener,noreferrer');
        else showToast(`Could not open that packet — ${fresh?.error || 'no link came back'}`, 'error');
    };
    const togglePacket = async (p) => {
        const have = pkOpen[p.id];
        if (have?.manifest) { setPkOpen((m) => ({ ...m, [p.id]: { ...m[p.id], show: !m[p.id].show } })); return; }
        const v = await fetchPacket(p.id);
        if (v?.manifest) setPkOpen((m) => ({ ...m, [p.id]: { ...m[p.id], show: true } }));
    };

    // ── Sources ──
    // The document's source choices (billingTicketDocs SRC_FIELDS) and the
    // note that says why. Stored on the document, so every build and every
    // archived packet reads the same ones.
    const src = useMemo(() => normalizeSrc(doc?.src), [doc && doc.src]); // deliberately narrow deps
    const [showSrc, setShowSrc] = useState(false);
    const [notesDraft, setNotesDraft] = useState(null);
    useEffect(() => { setNotesDraft(null); }, [selId]);

    // ── Supplement: who is on it ──
    // The people with hours on the supplement's days, read through
    // cellTickets (the same pull the packet makes), so the picker offers
    // only names that will print. Reloaded when its days or the day data
    // change; filled in a day at a time as the pulls land.
    const [crew, setCrew] = useState({ key: '', loading: false, people: [], error: '' });
    const crewKey = doc && isSupplementDoc(doc) && doc.cells.length ? `${doc.id}|${sortCells(doc.cells).join()}|${dataKey || ''}` : '';
    useEffect(() => {
        if (!crewKey) { setCrew({ key: '', loading: false, people: [], error: '' }); return undefined; }
        if (!cellTickets) return undefined;
        let dead = false;
        const cells = sortCells(doc.cells);
        // Union of the day tickets' people: id -> { id, name, cls, hours: { cell: total } }.
        const fold = (tickets) => {
            const m = {};
            cells.forEach((k) => (tickets?.[k]?.labor || []).forEach((p) => {
                const id = String(p.id || '');
                if (!id) return;
                const e = m[id] || (m[id] = { id, name: p.name || id, cls: p.cls || p.payClass || '', hours: {} });
                e.hours[k] = (e.hours[k] || 0) + (Number(p.total) || 0);
            }));
            return Object.values(m).sort((a, b) => String(a.name).localeCompare(String(b.name)));
        };
        setCrew({ key: crewKey, loading: true, people: [], error: '' });
        cellTickets(cells, (prog) => { if (!dead) setCrew({ key: crewKey, loading: true, people: fold(prog?.tickets || {}), error: '' }); })
            .then((t) => { if (!dead) setCrew({ key: crewKey, loading: false, people: fold(t || {}), error: '' }); })
            .catch((e) => { if (!dead) setCrew((c) => ({ ...c, key: crewKey, loading: false, error: String(e?.message || e) })); });
        return () => { dead = true; };
    }, [crewKey]); // deliberately narrow deps
    const togglePerson = (p) => {
        if (!doc || locked || !isSupplementDoc(doc)) return;
        const only = doc.only || [];
        const on = only.some((x) => String(x.id) === String(p.id));
        const next = on ? only.filter((x) => String(x.id) !== String(p.id)) : [...only, { id: String(p.id), name: String(p.name || '').slice(0, 80) }];
        persist({ ...doc, only: next, h: [...doc.h, stamp('People', `${p.name || p.id} ${on ? 'taken off' : 'put on'}`)] });
    };
    // Evidence preview: one backup file open inline under its row, so the
    // scan can be read before it is ticked onto the ticket.
    const [peek, setPeek] = useState('');
    useEffect(() => { setPeek(''); }, [selId]);
    const setSrc = (key, id) => {
        if (!doc || locked) return;
        const f = SRC_FIELDS.find((x) => x.key === key);
        const o = f?.options.find((x) => x.id === id);
        if (!f || !o || src[key] === id) return;
        persist({ ...doc, src: compactSrc({ ...src, [key]: id }), h: [...doc.h, stamp('Sources', `${f.label}: ${o.label}`)] });
    };
    const saveNotes = () => {
        if (!doc || notesDraft === null) return;
        const notes = String(notesDraft).slice(0, 600);
        setNotesDraft(null);
        if (notes === src.notes) return;
        persist({ ...doc, src: compactSrc({ ...src, notes }), h: [...doc.h, stamp('Sources', notes ? 'Notes updated' : 'Notes cleared')] });
    };
    // ── Backup ──
    const [fileQuery, setFileQuery] = useState('');
    const suggested = useMemo(() => (doc ? suggestBackup(files, doc.cells, fileDate) : []), [doc, files, fileDate]);
    const toggleFile = (f) => {
        if (!doc || locked) return;
        const on = doc.files.some((x) => x.id === f.id);
        const next = on ? doc.files.filter((x) => x.id !== f.id)
            : [...doc.files, { id: f.id, driveId: f.driveId, name: f.name, path: f.path || '' }];
        // Taken off stays off — auto-attach won't put it back.
        const skip = (doc.skip || []).filter((id) => String(id) !== String(f.id));
        persist({ ...doc, files: next, skip: on ? [...skip, f.id].slice(-200) : skip });
    };
    // The day's scans go on as soon as a day is on the ticket and SharePoint
    // has listed — no clicking through the suggestions. Runs again on each
    // listing sync, so a scan filed after the day was picked still lands
    // (until the check has run on that day; autoBackup).
    useEffect(() => {
        // A summary carries no backup.
        if (!doc || locked || filesLoading || isSummaryDoc(doc)) return;
        const fill = autoBackup(doc, files, fileDate);
        if (!fill) return;
        persist({ ...doc, files: [...doc.files, ...fill.files], af: fill.af });
        if (fill.files.length) showToast(`Pulled ${fill.files.length} scan${fill.files.length === 1 ? '' : 's'} from SharePoint as backup.`, 'success');
    }, [doc && doc.id, doc && doc.rev, doc && doc.cells.join(), files, filesLoading, locked]); // deliberately narrow deps

    // ── Uploads: backup from disk, and a signed copy ──
    const backupRef = useRef(null);
    const signedRef = useRef(null);
    const timecardRef = useRef(null);
    const [uploading, setUploading] = useState('');
    const firstDate = doc && doc.cells.length ? splitCell(sortCells(doc.cells)[0]).d : '';
    const lastDate = doc && doc.cells.length ? splitCell(sortCells(doc.cells)[doc.cells.length - 1]).d : '';
    const pickBackup = async (e) => {
        const list = Array.from(e.target.files || []);
        e.target.value = '';
        if (!doc || locked || !list.length || !uploadFile) return;
        if (!firstDate) { showToast('Put a day on the ticket first — the upload files under it.', 'warn'); return; }
        setUploading('backup');
        const added = [];
        try {
            for (const file of list) {
                if (file.size > MAX_DOC_BYTES) { showToast(`${file.name} is over 15 MB — skipped.`, 'warn'); continue; }
                const saved = await uploadFile(file, firstDate); // eslint-disable-line no-await-in-loop
                if (saved?.id) added.push({ id: saved.id, driveId: saved.driveId, name: saved.name, path: saved.path || '' });
            }
            if (added.length) {
                const have = new Set(doc.files.map((f) => f.id));
                persist({ ...doc, files: [...doc.files, ...added.filter((f) => !have.has(f.id))] });
                showToast(`${added.length} file${added.length === 1 ? '' : 's'} filed to SharePoint and put on this ticket as backup.`, 'success');
                onFilesChanged?.();
            }
        } catch (err) {
            showToast(`Upload failed — ${err?.message || err}`, 'error');
        } finally {
            setUploading('');
        }
    };
    // A ticket signed on paper: file the scan as the signed copy and mark
    // the document Signed — the same end state a completed envelope reaches.
    const pickSigned = async (e) => {
        const file = (e.target.files || [])[0];
        e.target.value = '';
        if (!doc || !file || !uploadFile || versionLocked) return;
        if (!lastDate) { showToast('Put a day on the ticket first.', 'warn'); return; }
        const ext = (/\.([a-z0-9]+)$/i.exec(file.name) || [])[1] || 'pdf';
        const name = `Signed ${docFileName(doc.cells, jobKey).replace(/\.pdf$/i, '')}.${ext.toLowerCase()}`;
        const dates = new Set(doc.cells.map((k) => splitCell(k).d));
        setUploading('signed');
        try {
            const saved = await uploadFile(file, lastDate, { name, level: dates.size > 1 ? 'week' : 'day' });
            persist({
                ...doc,
                st: 'Signed',
                signed: { name: saved.name, path: saved.path || '', url: saved.webUrl || '', t: moment().format('YYYY-MM-DD HH:mm'), u: String(userName || '').slice(0, 30) },
                h: [...doc.h, stamp('Signed', `Signed copy uploaded: ${saved.name}`)],
            });
            showToast(`Signed copy filed — ${saved.path || saved.name}`, 'success');
            onFilesChanged?.();
        } catch (err) {
            showToast(`Upload failed — ${err?.message || err}`, 'error');
        } finally {
            setUploading('');
        }
    };

    // ── Packet + send ──
    // Preview opens a tab; Save from Chrome's PDF viewer then names the file
    // after the blob's UUID, because a blob URL carries no filename. Download
    // goes through an anchor instead, which does, so the packet lands in
    // Downloads as “Field Tickets <job> <dates> <shifts>.pdf”.
    // part: 'all' is the signature document (summaries, tickets, backup);
    // 'summary' and 'tickets' are the two narrow exports beside it.
    const makePacket = async (save, part = 'all') => {
        if (!doc || busy) return;
        // The key carries the part so only the section that was pressed shows
        // a clock — three sends, three sets of buttons.
        startBusy(`${save ? 'download' : 'preview'}:${part}`);
        try {
            const pk = await buildPacket(doc, byCell, setPkStep, { part });
            const url = URL.createObjectURL(new Blob([pk.bytes], { type: 'application/pdf' }));
            if (save) {
                const a = document.createElement('a');
                a.href = url;
                a.download = pk.name || docFileName(doc.cells, jobKey);
                a.click();
            } else {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            if (pk.skipped?.length) showToast(`Backup left out of the packet: ${pk.skipped.join('; ')}`, 'warn');
            await archivePacket(doc, pk, part, save ? 'download' : 'preview');
        } catch (e) {
            showToast(`Packet failed — ${e?.message || e}`, 'error');
        } finally {
            endBusy();
        }
    };
    // Everything the client will be sent, in one PDF — what the approver
    // looks at in step 4 before the three sends go out separately.
    const previewPacket = () => makePacket(false);
    const downloadPacket = () => makePacket(true);
    const docDates = doc ? new Set(doc.cells.map((k) => splitCell(k).d)) : new Set();
    // What each covered day will be built from, under the current choices.
    // Cheap and read off live refs, so it is computed every render rather
    // than memoised on deps it cannot name.
    const srcDays = doc && daySources ? Object.fromEntries([...docDates].map((d) => [d, daySources(d, src)])) : {};
    // ── Which equipment basis this packet is on ──
    // HeavyJob's equipment lines until the week's time card has been reviewed
    // into its workbook; from then on the workbook, which is the only place
    // that says a unit sat on rent for the whole calendar day.
    const weekEndOf = (d) => {
        const m = moment(d, 'YYYY-MM-DD');
        return (m.day() === 0 ? m : m.clone().day(7)).format('YYYY-MM-DD');
    };
    const docWeeks = [...new Set([...docDates].map(weekEndOf))].sort();
    const weeksOnWorkbook = docWeeks.filter((w) => equipWeeks?.[w]);
    const pickTimecard = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !importTimecard || uploading || versionLocked) return;
        // One document rarely straddles two weeks; when it does, the workbook
        // being loaded is the one for its last day.
        const we = docWeeks[docWeeks.length - 1];
        setUploading('timecard');
        try {
            const units = await importTimecard(file, we);
            showToast(`Reviewed time card loaded — ${units.length} units bill off the workbook for WE ${md(we)}.`, 'success');
        } catch (err) {
            showToast(`Could not read that workbook — ${err?.message || err}`, 'error');
        } finally {
            setUploading('');
        }
    };
    const [form, setForm] = useState(null);
    const openForm = (part) => setForm({
        part,
        // name/email only matter when the job has no signer map at all.
        name: String(settings?.signerName || ''),
        email: String(settings?.signerEmail || ''),
        cc: String(settings?.signerCc || ''),
        subject: isSupplementDoc(doc)
            ? `Supplemental ${part === 'night' ? 'Night' : 'Day'} Field Tickets ${docName(partCells(doc.cells, part))} — ${onlyNames(doc)} — Job ${jobKey}`
            : part === 'summary'
                ? `Field Ticket Week Summary ${docName(doc.cells)} — Job ${jobKey}`
                : `${part === 'night' ? 'Night' : 'Day'} Field Tickets ${docName(partCells(doc.cells, part))} — Job ${jobKey}`,
        message: isSupplementDoc(doc)
            ? `Attached are supplemental ${part} field tickets for ${docName(partCells(doc.cells, part))} covering ${onlyNames(doc)}, who ${(doc.only || []).length === 1 ? 'was' : 'were'} left off the tickets already signed for ${(doc.only || []).length === 1 ? 'that day' : 'those days'}. The sign-in sheets are behind them. Please review and sign at your convenience.`
            : part === 'summary'
                ? `Attached is the week summary for ${docName(doc.cells)}. The day and night tickets behind it come as their own documents. Please review and sign at your convenience.`
                : `Attached are the ${part} field tickets for ${docName(partCells(doc.cells, part))}, with the signed sign-in sheets behind them. Please review and sign at your convenience.`,
        draft: false,
    });
    const submit = async (sendTo) => {
        if (!doc || !form || busy) return;
        const part = form.part || 'all';
        const recipients = partSigners(settings, doc, part);
        const toNames = sendTo.length
            ? sendTo.map((r) => r.name || r.email).join(', ')
            : String(form?.email || '').trim();
        if (testFlow) {
            if (!testTo.length) { showToast('Your login has no email to send the test to.', 'warn'); return; }
        } else {
            if (recipients.length && !sendTo.length) { showToast('Nobody is ticked to receive this — pick at least one signer.', 'warn'); return; }
            if (!recipients.length && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email || '').trim())) { showToast('A valid signer email is required.', 'warn'); return; }
        }
        startBusy(`send:${part}`);
        try {
            const pk = await buildPacket(doc, byCell, setPkStep, { part });
            setPkStep({ msg: 'handing the packet to DocuSign', done: 0, total: 0 });
            if (testFlow) {
                // Never persists a stage: a test envelope is not this ticket's.
                const t = await sendPacket(doc, { ...form, part, test: true }, pk);
                await archivePacket(doc, pk, part, 'test', {
                    sent: { rowId: t?.rowId || null, envelopeId: t?.envelopeId || null, status: t?.status || '', docId: testDocId(partDocId(doc.id, part)), to: (t?.signers || []).map((r) => r.email) },
                });
                showToast(form.draft ? 'Test draft created — open its document, then Send now.' : `Test sent to ${testTo[0].email} only.`, 'success');
                setForm(null);
                return;
            }
            const sent = await sendPacket(doc, { ...form, part, signers: sendTo }, pk);
            // The packet decides the final list: a shift with no hours prints
            // no page, so its rep has nothing to sign and comes off.
            const went = Array.isArray(sent?.signers) ? sent.signers : Array.isArray(sent) ? sent : [];
            const names = went.length ? went.map((r) => r.name || r.email).join(', ') : toNames;
            // The copy of exactly what DocuSign was handed, under the envelope
            // it went out as.
            await archivePacket(doc, pk, part, form.draft ? 'draft' : 'send', {
                sent: { rowId: sent?.rowId || null, envelopeId: sent?.envelopeId || null, status: sent?.status || '', docId: partDocId(doc.id, part), to: went.map((r) => r.email) },
            });
            if (!form.draft) {
                // The stage is the whole ticket's, and it reads "out" from the
                // first of the three sends; docStage settles it to Signed only
                // once every one of them is back.
                persist({ ...doc, st: 'Sent for Signature', h: [...doc.h, stamp('Sent for Signature', `${PART_TITLE[part] || 'Sent'} — DocuSign to ${names}`)] });
            }
            showToast(form.draft ? 'Draft envelope created — open its document, then Send now.' : `Sent to ${names} for signature.`, 'success');
            setForm(null);
        } catch (e) {
            showToast(`Send failed — ${e?.message || e}`, 'error');
        } finally {
            endBusy();
        }
    };
    // Every send records the HeavyJob values the ticket went out with (the
    // Billing tab's Reconciliation view). A ticket sent before that, or signed
    // on paper, is recorded here against the cards as they stand now.
    const recordSnapshot = async (part) => {
        if (!doc || !recordRecon || busy) return;
        startBusy(`recon:${part}`);
        try {
            setPkStep({ msg: 'reading HeavyJob and writing the snapshot', done: 0, total: 0 });
            const res = await recordRecon(doc, part);
            showToast(`HeavyJob snapshot recorded — ${res?.inserted ?? res?.rows ?? 0} rows. See Billing › Reconciliation.`, 'success');
        } catch (e) {
            showToast(`Could not record the snapshot — ${e?.message || e}`, 'error');
        } finally {
            endBusy();
        }
    };
    const removeDoc = () => {
        if (!doc || locked) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm(isSummaryDoc(doc)
            ? `Remove week summary ${letterOf(doc)} (${docName(doc.cells)})? Its days keep their tickets.`
            : isSupplementDoc(doc)
                ? `Remove supplement ${letterOf(doc)} (${docName(doc.cells)} — ${onlyNames(doc) || 'nobody picked'})? Its days keep their tickets.`
                : `Remove ticket ${letterOf(doc)} (${docName(doc.cells)})? Its days go back to uncovered.`)) return;
        persist({ ...doc, del: 1 });
        setSelId(null);
    };

    // ════════════════════════ render helpers ════════════════════════

    const stageLabel = (d) => docStage(d, partEnvelopes(d, sigByDoc)) || d.st;

    const fmtWhen = (t) => (t ? moment(t).format('M/D/YY HH:mm') : '');
    const link = (label, onPress, title) => (
        <Pressable onPress={onPress} title={title} style={{ marginLeft: 10, cursor: 'pointer' }}>
            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{label}</Text>
        </Pressable>
    );

    // One archived packet's manifest, opened under its row.
    const renderManifest = (m) => {
        const line = (label, text, color) => (text ? (
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', paddingTop: 1 }}>
                <Text style={{ fontSize: 10, color: '#94a3b8', width: 92, flexShrink: 0 }}>{label}</Text>
                <Text style={{ fontSize: 10, color: color || '#334155', flexShrink: 1 }}>{text}</Text>
            </View>
        ) : null);
        const days = m.sources && typeof m.sources === 'object' ? Object.keys(m.sources).sort() : [];
        return (
            <View style={{ marginTop: 4, marginLeft: 4, paddingLeft: 10, borderLeftWidth: 2, borderColor: '#e2e8f0' }}>
                {line('Covers', `${m.covers || ''}${m.stage ? ` · ticket was ${m.stage} when built` : ''}`)}
                {line('Built by', `${m.by || '—'}${m.byEmail ? ` <${m.byEmail}>` : ''} · ${fmtWhen(m.at)}${m.size ? ` · ${fmtBytes(m.size)}` : ''}${m.pages ? ` · ${m.pages} pages` : ''}`)}
                {m.sent ? line('Envelope', `${m.sent.envelopeId || (m.sent.rowId ? `row ${m.sent.rowId}` : '—')} · ${m.sent.status || ''}${m.sent.to?.length ? ` · to ${m.sent.to.join(', ')}` : ''}`, '#0f172a') : null}
                {line('Sources', m.src && srcIsCustom(m.src) ? `Custom — ${srcSummary(m.src)}` : 'Default order — reviewed card → roster → HeavyJob; Staff List for classes', m.src && srcIsCustom(m.src) ? '#b45309' : undefined)}
                {m.src?.notes ? line('Notes', m.src.notes) : null}
                {days.map((d) => {
                    const x = m.sources[d] || {};
                    return line(md(d), `${x.feed || '?'} feed · labor: ${x.labor || '?'} · split: ${x.shift || '?'} · classes: ${x.cls || '?'} · equipment: ${x.equip || '?'}${x.card?.name ? ` · card ${x.card.name}` : ''}${Math.abs(Number(x.mismatch) || 0) > 0.5 ? ` · HEET vs HJ ${Number(x.mismatch) > 0 ? '+' : ''}${Math.round(Number(x.mismatch) * 100) / 100}h` : ''}`);
                })}
                {Array.isArray(m.files) && m.files.length ? line('Backup', m.files.map((f) => `${f.name}${f.auto ? ' (auto)' : ''}`).join('; ')) : line('Backup', m.part === 'summary' ? 'none — a summary carries no backup' : 'none')}
                {Array.isArray(m.skipped) && m.skipped.length ? line('Left out', m.skipped.join('; '), '#b45309') : null}
                {Array.isArray(m.labels) && m.labels.length ? line('Signs', m.labels.join(', ')) : null}
                {line('Fingerprint', m.hash || '')}
            </View>
        );
    };

    // Archived packets, newest first. A build after a send says whether it
    // still matches what the client was handed — the same bytes give the
    // same fingerprint.
    const PURPOSE_LABEL = { preview: 'Preview', download: 'Download', send: 'SENT', draft: 'Draft envelope', test: 'Test send' };
    const renderPackets = (list, opts = {}) => {
        if (packets.error) return <Text style={{ fontSize: 11, color: '#b45309' }}>{`Packet archive unavailable — ${packets.error}`}</Text>;
        if (packets.loading && !list.length) return <Text style={{ fontSize: 11, color: '#94a3b8' }}>Reading the packet archive…</Text>;
        if (!list.length) return <Text style={{ fontSize: 11, color: '#94a3b8' }}>{opts.empty || 'No packets built for this ticket yet — every preview, download and send is kept here.'}</Text>;
        const sentByPart = {};
        list.forEach((p) => { const k = `${p.docId}|${p.part}`; if (p.purpose === 'send' && !sentByPart[k]) sentByPart[k] = p; });
        return list.slice(0, opts.limit || 60).map((p) => {
            const sent = sentByPart[`${p.docId}|${p.part}`];
            const rel = p.purpose === 'send' || !sent || !p.hash ? null
                : sent.hash === p.hash ? 'same as sent' : String(p.at) > String(sent.at) ? 'differs from what was sent' : 'before the send';
            const o = pkOpen[p.id];
            const owner = p.docId ? docById[p.docId] : null;
            return (
                <View key={p.id} style={{ borderTopWidth: 1, borderColor: '#f1f5f9', paddingTop: 5, paddingBottom: 5 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Text style={{ fontSize: 11, color: '#0f172a', fontWeight: '600', width: 104 }}>{fmtWhen(p.at) || '—'}</Text>
                        <Text style={{ fontSize: 10, fontWeight: '700', color: p.purpose === 'send' ? '#fff' : '#334155', backgroundColor: p.purpose === 'send' ? '#0f172a' : '#f1f5f9', borderRadius: 3, paddingLeft: 5, paddingRight: 5, paddingTop: 1, paddingBottom: 1, marginRight: 6 }}>
                            {PURPOSE_LABEL[p.purpose] || p.purpose}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#334155', marginRight: 6 }}>{PART_SHORT[p.part] || (p.part === 'all' ? 'Whole packet' : p.part === 'tickets' ? 'Day tickets' : p.part)}</Text>
                        {opts.showDoc ? (owner ? (
                            <Pressable onPress={() => { setSelId(owner.id); setEditCells(false); }} style={{ marginRight: 6, cursor: 'pointer' }}>
                                <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{`Ticket ${letterOf(owner)} · ${p.covers || docName(owner.cells)}`}</Text>
                            </Pressable>
                        ) : (
                            <Text style={{ fontSize: 11, color: '#94a3b8', marginRight: 6 }}>{`${p.covers || p.docId} (ticket removed)`}</Text>
                        )) : null}
                        <Text style={{ fontSize: 10, color: '#64748b', marginRight: 6 }}>{`${p.by || '—'} · ${fmtBytes(p.size)}${p.pages ? ` · ${p.pages} pp` : ''}`}</Text>
                        {rel ? (
                            <Text style={{ fontSize: 10, fontWeight: '600', color: rel === 'same as sent' ? '#047857' : rel.startsWith('differs') ? '#b45309' : '#94a3b8' }}>{rel}</Text>
                        ) : null}
                        <View style={{ flexGrow: 1 }} />
                        {link('Open', () => openPacket(p), 'Open this packet exactly as it was built')}
                        {link(o?.show ? 'Hide' : 'Details', () => togglePacket(p), 'What this packet was built from')}
                    </View>
                    {o?.show && o.manifest ? renderManifest(o.manifest)
                        : o?.loading ? <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Reading…</Text>
                            : o?.error ? <Text style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>{o.error}</Text> : null}
                </View>
            );
        });
    };

    const renderMap = () => (
        <View style={{ ...card, paddingTop: 8, paddingBottom: 8, width: 330, flexShrink: 0, maxHeight: '100%', overflow: 'auto' }}>
            <View style={{ flexDirection: 'row', paddingLeft: 10, paddingRight: 10, paddingBottom: 6, borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                <Text style={{ ...sectionLabel, width: 92 }}>WORK DAY</Text>
                {DOC_SHIFTS.map((sh) => <Text key={sh} style={{ ...sectionLabel, flexGrow: 1, flexBasis: 0, textAlign: 'center' }}>{sh.toUpperCase()}</Text>)}
            </View>
            {mapDays.map(({ d, hasDetail }) => (
                <View key={d} style={{ flexDirection: 'row', alignItems: 'center', paddingLeft: 10, paddingRight: 10, paddingTop: 2, paddingBottom: 2 }}>
                    <Text style={{ fontSize: 11, color: hasDetail ? '#0f172a' : '#94a3b8', fontWeight: '600', width: 92 }}>{fmtDay(d)}</Text>
                    {DOC_SHIFTS.map((sh) => {
                        const k = cellKey(d, sh);
                        const owner = cover[k];
                        const mine = doc && owner && owner.id === doc.id;
                        // The selected week summary or supplement rings its days over their tickets' tint.
                        const inSummary = Boolean(doc && sharesCells(doc) && doc.cells.includes(k));
                        const legacy = !owner ? legacyStatus?.(d, sh) : null;
                        const c = owner ? colorOf(owner) : null;
                        // Supplements on this cell ride as badges beside the owner's chip.
                        const sups = extras[k] || [];
                        return (
                            <View key={sh} style={{ flexGrow: 1, flexBasis: 0, marginLeft: 4, flexDirection: 'row', alignItems: 'center' }}>
                                <Pressable
                                    onPress={() => clickCell(d, sh, hasDetail)}
                                    title={owner ? `Ticket ${letterOf(owner)} — ${docName(owner.cells)} (${stageLabel(owner)})`
                                        : legacy ? `Not on a ticket — per-day sheet: ${legacy.label}` : doc && editCells ? 'Add to this ticket' : 'Start a ticket here'}
                                    style={{
                                        flexGrow: 1, flexBasis: 0, height: 22, borderRadius: 4, cursor: 'pointer',
                                        alignItems: 'center', justifyContent: 'center', flexDirection: 'row',
                                        borderWidth: mine || inSummary ? 2 : 1, borderStyle: owner || hasDetail || inSummary ? 'solid' : 'dashed',
                                        borderColor: inSummary ? colorOf(doc) : owner ? c : '#e2e8f0',
                                        backgroundColor: owner ? `${c}${mine ? '33' : '1a'}` : '#fff',
                                    }}
                                >
                                    {owner ? (
                                        <Text style={{ fontSize: 10, fontWeight: '700', color: c }}>
                                            {`${letterOf(owner)} · ${stageLabel(owner) === 'Sent for Signature' ? 'Sent' : stageLabel(owner)}`}
                                        </Text>
                                    ) : (
                                        <Text style={{ fontSize: 10, color: legacy ? '#64748b' : '#cbd5e1' }}>{legacy ? legacy.label : hasDetail ? '—' : ''}</Text>
                                    )}
                                </Pressable>
                                {sups.map((sd) => {
                                    const sc = colorOf(sd);
                                    const on = doc && doc.id === sd.id;
                                    return (
                                        <Pressable
                                            key={sd.id}
                                            onPress={() => { setSelId(sd.id); setEditCells(false); }}
                                            title={`Supplement ${letterOf(sd)} — ${onlyNames(sd) || 'nobody picked yet'} (${stageLabel(sd)})`}
                                            style={{
                                                marginLeft: 3, height: 22, minWidth: 22, paddingLeft: 4, paddingRight: 4, borderRadius: 4, cursor: 'pointer',
                                                alignItems: 'center', justifyContent: 'center',
                                                borderWidth: on ? 2 : 1, borderStyle: 'solid', borderColor: sc, backgroundColor: `${sc}${on ? '33' : '1a'}`,
                                            }}
                                        >
                                            <Text style={{ fontSize: 10, fontWeight: '700', color: sc }}>{`+${letterOf(sd)}`}</Text>
                                        </Pressable>
                                    );
                                })}
                            </View>
                        );
                    })}
                </View>
            ))}
            {!mapDays.length ? <Text style={{ fontSize: 12, color: '#94a3b8', padding: 10 }}>No work days yet.</Text> : null}
            {summaryDocs.length ? (
                <View style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 8, marginTop: 6, borderTopWidth: 1, borderColor: '#f1f5f9' }}>
                    <Text style={{ ...sectionLabel, marginBottom: 4 }}>WEEK SUMMARIES</Text>
                    {summaryDocs.map((s) => (
                        <Pressable
                            key={s.id}
                            onPress={() => { setSelId(s.id); setEditCells(false); }}
                            style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 2, paddingBottom: 2, cursor: 'pointer' }}
                        >
                            <View style={{ width: 16, height: 16, borderRadius: 999, backgroundColor: colorOf(s), alignItems: 'center', justifyContent: 'center', marginRight: 6 }}>
                                <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff' }}>{letterOf(s)}</Text>
                            </View>
                            <Text style={{ fontSize: 11, color: '#0f172a', fontWeight: doc && doc.id === s.id ? '700' : '400' }}>{docName(s.cells)}</Text>
                            <Text style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>{stageLabel(s) === 'Sent for Signature' ? 'Sent' : stageLabel(s)}</Text>
                        </Pressable>
                    ))}
                </View>
            ) : null}
            {supplementDocs.length ? (
                <View style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 8, marginTop: 6, borderTopWidth: 1, borderColor: '#f1f5f9' }}>
                    <Text style={{ ...sectionLabel, marginBottom: 4 }}>SUPPLEMENTS — PEOPLE A TICKET MISSED</Text>
                    {supplementDocs.map((s) => (
                        <Pressable
                            key={s.id}
                            onPress={() => { setSelId(s.id); setEditCells(false); }}
                            style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 2, paddingBottom: 2, cursor: 'pointer' }}
                        >
                            <View style={{ width: 16, height: 16, borderRadius: 999, backgroundColor: colorOf(s), alignItems: 'center', justifyContent: 'center', marginRight: 6 }}>
                                <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff' }}>{letterOf(s)}</Text>
                            </View>
                            <View style={{ flexShrink: 1 }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <Text style={{ fontSize: 11, color: '#0f172a', fontWeight: doc && doc.id === s.id ? '700' : '400' }}>{docName(s.cells)}</Text>
                                    <Text style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>{stageLabel(s) === 'Sent for Signature' ? 'Sent' : stageLabel(s)}</Text>
                                </View>
                                <Text style={{ fontSize: 10, color: '#64748b' }}>{onlyNames(s) || 'nobody picked yet'}</Text>
                            </View>
                        </Pressable>
                    ))}
                </View>
            ) : null}
        </View>
    );

    // The lettered chips duplicated the map, which already shows which ticket owns
    // each day — only the empty-state hint is worth keeping.
    const renderDocList = () => (
        liveDocs.length ? null : (
            <Text style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                No tickets yet. Click a day/shift on the map to start one — then add the other days it covers.
            </Text>
        )
    );

    const renderFiles = () => {
        const chosen = new Set(doc.files.map((f) => f.id));
        const q = fileQuery.trim().toLowerCase();
        const pool = q
            ? (files || []).filter((f) => `${f.path || ''}/${f.name}`.toLowerCase().includes(q)).slice(0, 40)
            : suggested.filter((f) => !chosen.has(f.id));
        const row = (f, on) => (
            <View key={f.id}>
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 3, paddingBottom: 3 }}>
                <Pressable onPress={() => toggleFile(f)} disabled={locked} style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 1, cursor: locked ? 'default' : 'pointer' }}>
                    <View style={{ width: 13, height: 13, borderRadius: 3, borderWidth: 1.5, borderStyle: 'solid', borderColor: on ? '#0f172a' : '#94a3b8', backgroundColor: on ? '#0f172a' : '#fff', alignItems: 'center', justifyContent: 'center', marginRight: 7 }}>
                        {on ? <Text style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</Text> : null}
                    </View>
                    <Text style={{ fontSize: 12, color: '#0f172a', flexShrink: 1 }}>{f.name}</Text>
                    {on && f.auto ? (
                        <Text title="Pulled from SharePoint automatically — untick to take it off" style={{ fontSize: 9, fontWeight: '700', color: '#64748b', borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 3, paddingLeft: 3, paddingRight: 3, marginLeft: 6 }}>AUTO</Text>
                    ) : null}
                    {f.path ? <Text style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6, flexShrink: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.path}</Text> : null}
                </Pressable>
                <View style={{ flexGrow: 1 }} />
                <Pressable onPress={() => setPeek((id) => (id === f.id ? '' : f.id))} title="Read the file here, under this row" style={{ marginLeft: 8, cursor: 'pointer' }}>
                    <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{peek === f.id ? 'Hide preview' : 'Preview'}</Text>
                </Pressable>
                <Pressable onPress={() => window.open(fileUrl(f), '_blank', 'noopener,noreferrer')} title="Open in a new tab" style={{ marginLeft: 10, cursor: 'pointer' }}>
                    <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>View</Text>
                </Pressable>
            </View>
            {peek === f.id ? (
                <View style={{ marginBottom: 6 }}>
                    {createElement('iframe', {
                        src: fileUrl(f),
                        title: f.name,
                        style: { width: '100%', height: 560, border: '1px solid #e2e8f0', borderRadius: 4, backgroundColor: '#fff', display: 'block' },
                    })}
                    <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{`${f.name}${f.path ? ` — ${f.path}` : ''}${on ? ' · on this ticket' : ' · not on this ticket yet — tick the box to attach it'}`}</Text>
                </View>
            ) : null}
            </View>
        );
        const bare = filesLoading ? [] : cellsWithoutBackup(doc, fileDate);
        return (
            <View>
                {doc.files.map((f) => row(f, true))}
                {!doc.cells.length && !doc.files.length ? <Text style={{ fontSize: 12, color: '#64748b', paddingBottom: 4 }}>Put days on the ticket and their sign-in scans come in from SharePoint on their own.</Text> : null}
                {doc.cells.length > 0 && !doc.files.length && filesLoading ? <Text style={{ fontSize: 12, color: '#64748b', paddingBottom: 4 }}>Looking in SharePoint for these days&apos; scans…</Text> : null}
                {!locked && bare.length ? (
                    <Text style={{ fontSize: 12, color: '#b45309', paddingBottom: 4 }}>
                        {`No scan filed in 02.06 - Timecards for ${bare.map((k) => { const { d, sh } = splitCell(k); return `${fmtDay(d)} ${sh}`; }).join(', ')}${doc.files.length ? '' : ' — the check will fail without backup'}. Upload it, or search below.`}
                    </Text>
                ) : null}
                {!locked ? (
                    <>
                        <TextInput
                            value={fileQuery}
                            onChangeText={setFileQuery}
                            placeholder={filesLoading ? 'Loading SharePoint…' : 'Search every file in 02.06 - Timecards'}
                            style={{ fontSize: 12, borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 4, paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8, marginTop: 6, marginBottom: 4 }}
                        />
                        {pool.length ? <Text style={{ ...sectionLabel, paddingTop: 2 }}>{q ? 'MATCHES' : 'SUGGESTED FOR THESE DAYS'}</Text> : null}
                        {pool.map((f) => row(f, chosen.has(f.id)))}
                        {!pool.length && !q && doc.cells.length > 0 && !filesLoading && !bare.length ? <Text style={{ fontSize: 11, color: '#94a3b8' }}>Nothing else dated for these days — search above for anything filed elsewhere.</Text> : null}
                    </>
                ) : null}
            </View>
        );
    };

    const field = (label, key, multiline) => (
        <View style={{ marginBottom: 6 }}>
            <Text style={{ ...sectionLabel, marginBottom: 2 }}>{label}</Text>
            <TextInput
                value={String(form[key] ?? '')}
                onChangeText={(v) => setForm((f) => ({ ...f, [key]: v }))}
                multiline={multiline}
                style={{ fontSize: 12, borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 4, paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8, ...(multiline ? { height: 56 } : {}) }}
            />
        </View>
    );

    // One row per week: daily tickets, week summary, invoice, and what is
    // owed next. Text tags only — the stage words carry the meaning.
    const TAG = {
        Signed: ['SIGNED', '#047857'], Approved: ['APPROVED', '#047857'],
        Out: ['OUT', '#1e40af'], 'Sent for Signature': ['OUT', '#1e40af'], 'Out for approval': ['OUT', '#1e40af'],
        Partial: ['PART', '#b45309'], Declined: ['DECLINED', '#991b1b'],
        Draft: ['DRAFT', '#64748b'], Validated: ['DRAFT', '#64748b'], None: ['NONE', '#94a3b8'],
    };
    const tag = (stage) => {
        const [label, color] = TAG[stage] || [String(stage || '').toUpperCase(), '#64748b'];
        return <Text style={{ fontSize: 9, fontWeight: '700', letterSpacing: 0.4, color, borderWidth: 1, borderStyle: 'solid', borderColor: color, borderRadius: 3, paddingLeft: 4, paddingRight: 4, paddingTop: 1, paddingBottom: 1, marginRight: 6 }}>{label}</Text>;
    };
    const stampDay = (utc) => (utc ? moment.utc(utc).local().format('M/D') : '');
    const renderWeeks = () => {
        if (!weekRows.length) return null;
        const col = { tickets: 190, summary: 210, invoice: 170 };
        const cell = (w, children) => <View style={{ width: w, flexShrink: 0, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', paddingRight: 8 }}>{children}</View>;
        const note = { fontSize: 11, color: '#475569' };
        return (
            <View style={{ marginTop: 16, borderTopWidth: 1, borderColor: '#f1f5f9', paddingTop: 8 }}>
                <Text style={{ ...sectionLabel, marginBottom: 3 }}>WEEK TRACKER — TICKETS, SUMMARY, INVOICE</Text>
                <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                    One row per week ending. Daily tickets count each day and shift by its crew send; the summary and the invoice are read off their own envelopes.
                </Text>
                <View style={{ flexDirection: 'row', paddingBottom: 3 }}>
                    <Text style={{ ...sectionLabel, width: 86 }}>WEEK ENDING</Text>
                    <Text style={{ ...sectionLabel, width: col.tickets }}>DAILY TICKETS</Text>
                    <Text style={{ ...sectionLabel, width: col.summary }}>WEEK SUMMARY</Text>
                    <Text style={{ ...sectionLabel, width: col.invoice }}>INVOICE</Text>
                    <Text style={{ ...sectionLabel }}>NEXT</Text>
                </View>
                {weekRows.map((w) => {
                    const t = w.tickets;
                    const counts = [
                        t.total ? `${t.signed}/${t.total} signed` : 'no tickets',
                        t.out ? `${t.out} out` : '', t.draft ? `${t.draft} draft` : '', t.declined ? `${t.declined} declined` : '',
                        t.untracked ? `${t.untracked} day${t.untracked === 1 ? '' : 's'} not ticketed` : '',
                    ].filter(Boolean).join(' · ');
                    const s = w.summary;
                    const sDoc = s ? docById[s.docId] : null;
                    return (
                        <View key={w.weekEnd} style={{ flexDirection: 'row', alignItems: 'center', borderTopWidth: 1, borderColor: '#f1f5f9', paddingTop: 4, paddingBottom: 4 }}>
                            <Text style={{ fontSize: 11, fontWeight: '600', color: '#0f172a', width: 86 }}>{moment(w.weekEnd, 'YYYY-MM-DD').format('M/D/YY')}</Text>
                            {cell(col.tickets, <>{tag(t.stage)}<Text style={note} title={t.untrackedDays.map(md).join(', ')}>{counts}</Text></>)}
                            {cell(col.summary, s ? (
                                <>
                                    {tag(s.stage)}
                                    {sDoc ? (
                                        <Pressable onPress={() => { setSelId(sDoc.id); setEditCells(false); }} style={{ cursor: 'pointer' }} title={s.signers ? `Signers: ${s.signers}` : 'Open this summary'}>
                                            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{`${s.name}${stampDay(s.at) ? ` · ${stampDay(s.at)}` : ''}`}</Text>
                                        </Pressable>
                                    ) : <Text style={note}>{s.name}</Text>}
                                </>
                            ) : <>{tag('None')}<Text style={note}>not sent</Text></>)}
                            {cell(col.invoice, w.invoices.length ? w.invoices.map((i) => (
                                <View key={i.rowId || i.no} style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    {tag(i.stage)}
                                    <Text style={note} title={`${md(i.from)}–${md(i.to)}`}>{i.no}</Text>
                                </View>
                            )) : <>{tag('None')}<Text style={note}>not invoiced</Text></>)}
                            <Text style={{ fontSize: 11, color: '#0f172a', flexShrink: 1 }}>{w.next}</Text>
                        </View>
                    );
                })}
            </View>
        );
    };

    const renderDoc = () => {
        if (!doc) {
            return (
                <View>
                    <Text style={{ fontSize: 12, color: '#64748b' }}>
                        Pick a ticket on the map, or click an uncovered day/shift to start one.
                        A ticket can cover any days and shifts — the first days with no night crew,
                        a weekend for both crews — and goes out as one signature packet.
                    </Text>
                    {renderWeeks()}
                    <View style={{ marginTop: 16, borderTopWidth: 1, borderColor: '#f1f5f9', paddingTop: 8 }}>
                        <Text style={{ ...sectionLabel, marginBottom: 3 }}>PACKET ARCHIVE — EVERYTHING BUILT ON THIS JOB</Text>
                        <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                            Every preview, download, send and test send, newest first, with what it was built from. Open reads the PDF as it was; Details reads its manifest.
                        </Text>
                        {renderPackets(packets.list, { showDoc: true, empty: 'Nothing archived on this job yet — the next packet built here lands in the list.' })}
                    </View>
                </View>
            );
        }
        const c = colorOf(doc);
        // A supplement builds nothing until somebody is picked to be on it.
        const noPeople = isSupplementDoc(doc) && !(doc.only || []).length;
        const step = (n, title, right) => (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 14, marginBottom: 6 }}>
                <View style={{ width: 18, height: 18, borderRadius: 999, backgroundColor: '#0f172a', alignItems: 'center', justifyContent: 'center', marginRight: 8 }}>
                    <Text style={{ fontSize: 10, color: '#fff', fontWeight: '700' }}>{n}</Text>
                </View>
                <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a' }}>{title}</Text>
                <View style={{ flexGrow: 1 }} />
                {right}
            </View>
        );

        // The build clock for whichever send is working — a week's packet is
        // minutes of day pulls, ticket drawing and backup binding, so a
        // silent button reads as a hang.
        const renderBusy = () => (
            <View style={{ marginTop: 6 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600', marginRight: 6 }}>
                        {`${BUSY_LABEL[String(busy).split(':')[0]] || 'Working'} — ${pkStep?.msg || 'starting'}…`}
                    </Text>
                    <Text style={{ fontSize: 11, color: '#94a3b8' }}>
                        {`${pkStep?.total ? `${Math.min(pkStep.done + 1, pkStep.total)} of ${pkStep.total} · ` : ''}${Math.floor(pkSecs / 60)}:${String(pkSecs % 60).padStart(2, '0')}`}
                    </Text>
                </View>
                {pkStep?.total ? (
                    <View style={{ height: 3, borderRadius: 2, backgroundColor: '#e2e8f0', marginTop: 4, maxWidth: 420 }}>
                        <View style={{ height: 3, borderRadius: 2, backgroundColor: '#1e40af', width: `${Math.round((pkStep.done / pkStep.total) * 100)}%` }} />
                    </View>
                ) : null}
                <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
                    A day that has aged out of HeavyJob can take a minute on its own; leave this open.
                </Text>
            </View>
        );

        // The envelope's subject, message and copies, opened by one send's
        // Send button and carrying that send's recipients.
        const renderForm = (part, to) => {
            const names = to.length ? to.map((r) => r.name || r.email).join(', ') : String(form?.email || '').trim();
            const cells = partCells(doc.cells, part);
            const pdf = part === 'summary'
                ? docFileName(cells, jobKey).replace(/\.pdf$/i, ' Week Summary.pdf')
                : docFileName(cells, jobKey);
            // One date on the document files into that day's folder; a
            // multi-day one into the week folder — the rule the webhook
            // applies when it files the signed copy (billingSignature.js).
            const oneDay = (pdf.match(/\d{1,2}\.\d{1,2}\.\d{2}/g) || []).length <= 1;
            const firstDay = splitCell(cells[0] || '').d;
            return (
                <View style={{ ...card, marginTop: 8, paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12, ...(testFlow ? { borderColor: '#f59e0b' } : {}) }}>
                    {/* What is actually about to leave the building: the PDF
                        by name, who it goes to by name and role, and where
                        the signed copy lands when they are done. */}
                    <View style={{ borderLeftWidth: 3, borderColor: testFlow ? '#f59e0b' : '#0f172a', paddingLeft: 9, marginBottom: 8 }}>
                        <Text style={{ fontSize: 11, color: '#0f172a', fontWeight: '700', marginBottom: 2 }}>{PART_TITLE[part]}</Text>
                        <Text style={{ fontSize: 11, color: '#334155' }}>{`Document: ${testFlow ? `TEST - ${pdf}` : pdf}`}</Text>
                        <Text style={{ fontSize: 11, color: '#334155' }}>{`Covers: ${docName(cells)}`}</Text>
                        <Text style={{ fontSize: 11, color: '#334155' }}>
                            {testFlow
                                ? `Goes to: ${testTo[0]?.email || 'nobody — no email on your login'} (you, holding every signature line)`
                                : `Goes to: ${to.length
                                    ? to.map((r) => `${r.name || r.email} <${r.email}> · ${r.role === 'All' ? 'signs all' : `${r.role} tickets`}`).join('; ')
                                    : `${String(form.name || '').trim() || 'the signer'} <${String(form.email || '').trim() || '—'}>`}`}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#334155' }}>
                            {testFlow
                                ? 'When signed: nothing is filed and this ticket does not move — a test envelope is not the ticket’s.'
                                : `When signed: files into SharePoint 02.06 - Timecards ${oneDay && firstDay ? `under ${md(firstDay)}` : `under the week folder for ${docWeeks.map(md).join(', ')}`} as "Signed ${pdf.replace(/\.pdf$/i, '')}.pdf", and ${part === 'summary' ? 'the summary' : `the ${part} crew’s`} days read Signed on the tracker.`}
                        </Text>
                    </View>
                    {!testFlow && !to.length ? field('Signer name', 'name') : null}
                    {!testFlow && !to.length ? field('Signer email', 'email') : null}
                    {!testFlow ? field('Copy to (comma separated)', 'cc') : null}
                    {field('Subject', 'subject')}
                    {field('Message', 'message', true)}
                    <Pressable onPress={() => setForm((f) => ({ ...f, draft: !f.draft }))} style={{ flexDirection: 'row', alignItems: 'center', cursor: 'pointer', marginBottom: 8 }}>
                        <View style={{ width: 12, height: 12, borderRadius: 3, borderWidth: 1.5, borderStyle: 'solid', borderColor: form.draft ? '#0f172a' : '#94a3b8', backgroundColor: form.draft ? '#0f172a' : '#fff', marginRight: 7, alignItems: 'center', justifyContent: 'center' }}>
                            {form.draft ? <Text style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</Text> : null}
                        </View>
                        <Text style={{ fontSize: 11, color: '#475569' }}>Create as a draft first — nothing is emailed until you press Send now</Text>
                    </Pressable>
                    <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                        {testFlow
                            ? `TEST — DocuSign emails ${testTo[0]?.email || 'you'} only, subject marked [TEST]. No client gets anything and this ticket's status does not change.`
                            : form.draft
                                ? 'A draft envelope is created in DocuSign; open its document above, then Send now.'
                                : `DocuSign emails ${names || 'the signer'} straight away — each signs their own line, in any order. This envelope covers ${docName(partCells(doc.cells, part))}${part === 'summary' ? ' as one summary' : ''}; the other sends on this ticket go out on their own.`}
                    </Text>
                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Pressable onPress={() => setForm(null)} style={{ paddingLeft: 12, paddingRight: 12, cursor: 'pointer' }}><Text style={{ fontSize: 12, color: '#475569', fontWeight: '600' }}>Cancel</Text></Pressable>
                        <Pressable onPress={() => submit(to)} disabled={String(busy).startsWith('send')} style={btnDark(String(busy).startsWith('send'))}>
                            <Text style={{ fontSize: 12, color: '#fff', fontWeight: '700' }}>{String(busy).startsWith('send') ? 'Sending…' : testFlow ? (form.draft ? 'Create Test Draft' : 'Send Test to Me') : form.draft ? 'Create Draft' : 'Send for Signature'}</Text>
                        </Pressable>
                    </View>
                </View>
            );
        };

        // Step 3 — where the packet will pull from, day by day, the choices
        // that change it, the note that says why, and the readiness checks.
        // The checks are advice, not a gate: the person building the ticket
        // reads them and decides.
        const renderSources = () => {
            const checks = readinessChecks(doc, srcDays, roster, fileDate);
            const fails = checks.filter((x) => x.level === 'fail').length;
            const warns = checks.filter((x) => x.level === 'warn').length;
            const LEVEL = { fail: '#b91c1c', warn: '#b45309', info: '#94a3b8' };
            const dates = [...docDates].sort();
            const cardFiles = [...new Map(dates.map((d) => srcDays[d]?.cardFile).filter(Boolean).map((f) => [f.we, f])).values()];
            const modLine = (f) => [f?.modifiedBy, f?.lastModified ? fmtWhen(f.lastModified) : ''].filter(Boolean).join(' ');
            const custom = srcIsCustom(src);
            // Two fixed columns, then the rest share the width and wrap —
            // the modal is wide, but the split column reads three sources.
            const col = (w, extra = {}) => (w > 20
                ? { fontSize: 10, color: '#334155', width: w, flexShrink: 0, paddingRight: 6, ...extra }
                : { fontSize: 10, color: '#334155', flexGrow: w, flexBasis: 0, minWidth: 0, flexShrink: 1, paddingRight: 6, ...extra });
            const head = (w, t) => <Text key={t} style={{ ...sectionLabel, fontSize: 9, paddingRight: 6, ...(w > 20 ? { width: w, flexShrink: 0 } : { flexGrow: w, flexBasis: 0, minWidth: 0 }) }}>{t}</Text>;
            return (
                <View>
                    {step(3, 'Sources & checks', (
                        <Pressable onPress={() => setShowSrc((v) => !v)} style={{ cursor: 'pointer' }}>
                            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{showSrc ? 'Hide detail' : 'Show detail & options'}</Text>
                        </Pressable>
                    ))}
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                        <Text style={{ fontSize: 11, color: custom ? '#b45309' : '#047857', fontWeight: '600', marginRight: 10 }}>
                            {custom ? `Custom — ${srcSummary(src)}` : 'Default order — approved sources first: reviewed time card → attendance roster → HeavyJob; Staff List for classes'}
                        </Text>
                        <Text style={{ fontSize: 11, color: fails ? '#b91c1c' : warns ? '#b45309' : '#047857', fontWeight: '600' }}>
                            {fails ? `${fails} blocker${fails === 1 ? '' : 's'}` : warns ? `${warns} warning${warns === 1 ? '' : 's'}` : 'Ready to build'}
                        </Text>
                    </View>
                    {checks.filter((x) => showSrc || x.level !== 'info').map((x, i) => (
                        <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', paddingTop: 2, paddingBottom: 2 }}>
                            <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: LEVEL[x.level], marginTop: 4, marginRight: 7, flexShrink: 0 }} />
                            <View style={{ flexShrink: 1 }}>
                                <Text style={{ fontSize: 11, color: '#0f172a' }}>{x.text}</Text>
                                {x.fix ? <Text style={{ fontSize: 10, color: '#64748b' }}>{x.fix}</Text> : null}
                            </View>
                        </View>
                    ))}
                    {!showSrc && src.notes ? (
                        <Text style={{ fontSize: 11, color: '#334155', marginTop: 4 }}>{`Notes: ${src.notes}`}</Text>
                    ) : null}
                    {showSrc ? (
                        <View style={{ ...card, marginTop: 8, paddingTop: 8, paddingBottom: 10, paddingLeft: 12, paddingRight: 12 }}>
                            {/* The choices. Each row is one question; the chosen answer's
                                hint says what it does in plain words. */}
                            <Text style={{ ...sectionLabel, marginBottom: 4 }}>WHERE TO PULL FROM</Text>
                            {SRC_FIELDS.map((f) => {
                                const chosen = f.options.find((o) => o.id === src[f.key]) || f.options[0];
                                return (
                                    <View key={f.key} style={{ paddingTop: 3, paddingBottom: 5 }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                            <Text style={{ fontSize: 11, color: '#0f172a', fontWeight: '600', width: 122, flexShrink: 0, paddingTop: 3 }}>{f.label}</Text>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', flexShrink: 1, flexGrow: 1 }}>
                                            {f.options.map((o) => {
                                                const on = src[f.key] === o.id;
                                                return (
                                                    <Pressable
                                                        key={o.id}
                                                        onPress={() => setSrc(f.key, o.id)}
                                                        disabled={locked}
                                                        title={o.hint}
                                                        style={{ borderWidth: 1, borderColor: on ? '#0f172a' : '#cbd5e1', backgroundColor: on ? '#0f172a' : '#fff', borderRadius: 999, paddingTop: 2, paddingBottom: 2, paddingLeft: 9, paddingRight: 9, marginRight: 4, marginBottom: 2, cursor: locked ? 'default' : 'pointer', opacity: locked && !on ? 0.45 : 1 }}
                                                    >
                                                        <Text style={{ fontSize: 10, fontWeight: '600', color: on ? '#fff' : '#334155' }}>{o.label}</Text>
                                                    </Pressable>
                                                );
                                            })}
                                            </View>
                                        </View>
                                        <Text style={{ fontSize: 10, color: '#64748b', marginLeft: 122, marginTop: 1 }}>{chosen.hint}</Text>
                                    </View>
                                );
                            })}
                            {locked ? <Text style={{ fontSize: 10, color: '#94a3b8' }}>Frozen with the ticket — an envelope is out on it.</Text> : null}

                            {/* Day by day, what the build will actually use right now. */}
                            <Text style={{ ...sectionLabel, marginTop: 10, marginBottom: 3 }}>DAY BY DAY — WHAT THE BUILD USES</Text>
                            <View style={{ flexDirection: 'row', paddingBottom: 3, borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                                {[[72, 'DAY'], [66, 'HOURS FEED'], [4, 'LABOR HOURS'], [6, 'DAY / NIGHT SPLIT'], [5, 'CLASSES'], [3, 'EQUIPMENT'], [4, 'FLAGS']].map(([w, t]) => head(w, t))}
                            </View>
                            {dates.map((d) => {
                                const x = srcDays[d] || {};
                                const flags = [
                                    ...(x.cardLoading ? ['card loading'] : []),
                                    ...(x.cardError ? [`card read failed`] : []),
                                    ...(Math.abs(Number(x.mismatch) || 0) > 0.5 ? [`HEET vs HJ ${Number(x.mismatch) > 0 ? '+' : ''}${Math.round(Number(x.mismatch) * 100) / 100}h`] : []),
                                    ...(x.manual ? [`${x.manual} manual shift fix${x.manual === 1 ? '' : 'es'}`] : []),
                                ];
                                const feedColor = x.feed === 'none' ? '#b91c1c' : x.feed === 'HeavyJob' ? '#b45309' : '#334155';
                                const laborColor = /refused/.test(String(x.labor || '')) ? '#b91c1c' : /reviewed/.test(String(x.labor || '')) ? '#047857' : '#334155';
                                return (
                                    <View key={d} style={{ flexDirection: 'row', alignItems: 'flex-start', paddingTop: 3, paddingBottom: 3, borderBottomWidth: 1, borderColor: '#f8fafc' }}>
                                        <Text style={col(72, { fontWeight: '600', color: '#0f172a' })}>{fmtDay(d)}</Text>
                                        <Text style={col(66, { color: feedColor })}>{x.feed === 'actuals' ? 'actual cost' : x.feed === 'none' ? 'no hours' : x.feed || '—'}</Text>
                                        <Text style={col(4, { color: laborColor })} title={x.card?.name ? `${x.card.name}${modLine(x.card) ? ` — ${modLine(x.card)}` : ''}` : undefined}>{x.labor || '—'}</Text>
                                        <Text style={col(6)}>{x.shift || '—'}</Text>
                                        <Text style={col(5)}>{x.cls || '—'}</Text>
                                        <Text style={col(3, { color: x.equip === 'reviewed workbook' ? '#047857' : '#334155' })}>{x.equip || '—'}</Text>
                                        <Text style={col(4, { color: flags.length ? '#b45309' : '#94a3b8' })}>{flags.join(' · ') || '—'}</Text>
                                    </View>
                                );
                            })}
                            <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
                                Order reads left to right within a cell: the first source that has a value wins; manual corrections always win.
                            </Text>

                            {/* The files behind those sources, by name and date, so
                                "the reviewed card" is a thing that can be opened. */}
                            <Text style={{ ...sectionLabel, marginTop: 10, marginBottom: 3 }}>FILES IN USE</Text>
                            {cardFiles.map((f) => (
                                <Text key={f.we} style={{ fontSize: 10, color: '#334155', paddingTop: 1 }}>{`Reviewed time card WE ${md(f.we)}: ${f.name || '(unnamed)'}${modLine(f) ? ` — ${modLine(f)}` : ''}`}</Text>
                            ))}
                            {!cardFiles.length ? <Text style={{ fontSize: 10, color: '#94a3b8', paddingTop: 1 }}>Reviewed time card: none filed for these weeks yet (02.06 - Timecards / WE folder, Timecards_&lt;job&gt;_WE&lt;m.d.yy&gt;.xlsx).</Text> : null}
                            <Text style={{ fontSize: 10, color: roster?.error ? '#b91c1c' : '#334155', paddingTop: 1 }}>
                                {roster?.error ? `Attendance roster: could not be read — ${roster.error}`
                                    : roster?.loading ? 'Attendance roster: reading…'
                                        : roster?.found ? `Attendance roster: ${sourceFiles?.roster?.name || 'Attendance & Force Report - Master'}${sourceFiles?.roster?.lastModified ? ` — ${fmtWhen(sourceFiles.roster.lastModified)}` : ''}`
                                            : 'Attendance roster: none on this job (02.05 - Daily Force Report)'}
                            </Text>
                            <Text style={{ fontSize: 10, color: '#334155', paddingTop: 1 }}>
                                {sourceFiles?.staffList?.name
                                    ? `Staff List classifications: ${sourceFiles.staffList.name}${sourceFiles.staffList.lastModified ? ` — ${fmtWhen(sourceFiles.staffList.lastModified)}` : ''}`
                                    : 'Staff List classifications: no Multi Phase Field Ticket workbook found in 02.06 - Timecards'}
                            </Text>
                            <Text style={{ fontSize: 10, color: '#334155', paddingTop: 1 }}>
                                {weeksOnWorkbook.length
                                    ? `Reviewed equipment workbook loaded for WE ${weeksOnWorkbook.map(md).join(', ')}${docWeeks.length > weeksOnWorkbook.length ? ` — not for WE ${docWeeks.filter((w) => !weeksOnWorkbook.includes(w)).map(md).join(', ')}` : ''}`
                                    : 'Reviewed equipment workbook: none loaded — equipment is HeavyJob’s (load it under Sending below)'}
                            </Text>
                            <Text style={{ fontSize: 10, color: '#334155', paddingTop: 1 }}>{`Backup: ${doc.files.length} file${doc.files.length === 1 ? '' : 's'} on this ticket, ${doc.files.filter((f) => f.auto).length} pulled automatically`}</Text>

                            {/* Why. Kept on the ticket and in every archived packet's
                                manifest; never printed on anything the client sees. */}
                            <Text style={{ ...sectionLabel, marginTop: 10, marginBottom: 2 }}>NOTES — WHY THIS TICKET IS CUT THIS WAY</Text>
                            <TextInput
                                value={notesDraft === null ? src.notes : notesDraft}
                                onChangeText={(v) => !locked && setNotesDraft(v)}
                                onBlur={saveNotes}
                                editable={!locked}
                                multiline
                                placeholder="e.g. WE 9/20 card leaves 9/17 orientation hours out on purpose · James signs weekdays only · 9/15 night crew keyed as Shift 1, corrected by hand"
                                style={{ fontSize: 11, borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 4, paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8, height: 52, backgroundColor: locked ? '#f8fafc' : '#fff' }}
                            />
                            <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                {`Saved when you click away. Kept on this ticket and in every archived packet’s manifest — never printed on the client’s document. ${(notesDraft === null ? src.notes : notesDraft).length}/600`}
                            </Text>
                        </View>
                    ) : null}
                </View>
            );
        };

        // One of the three sends: what is in it, who signs it, where its
        // envelope stands, and the buttons that build or send it. Each part
        // is its own DocuSign envelope under its own Doc_ID (partDocId), so
        // the day crew's week can be signed while the night crew's is still
        // being checked.
        const renderSend = (part, n) => {
            const cells = partCells(doc.cells, part);
            const to = partSigners(settings, doc, part);
            const on = to.filter((r) => !offSigners[offKey(part, r)]);
            const pEnv = envs[part] || null;
            const pTestEnv = sigByDoc?.[testDocId(partDocId(doc.id, part))] || null;
            const pLocked = Boolean(versionLocked || docLocked(doc, pEnv));
            // The only thing that can stop a send now is having nobody on it —
            // nobody to sign it, or, on a supplement, nobody picked to be on it.
            const off = (to.length > 0 && on.length === 0) || noPeople;
            const mine = (what) => busy === `${what}:${part}`;
            const formHere = Boolean(form && form.part === part);
            return (
                <View key={part}>
                    {step(n, PART_TITLE[part])}
                    <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                        {isSupplementDoc(doc)
                            ? `${docFileName(cells, jobKey, { supplement: true })} — the ${part} ticket${cells.length === 1 ? '' : 's'} for ${onlyNames(doc) || 'the people picked above'} only, nobody else and no equipment, then the backup pages matched to ${cells.length === 1 ? 'that day' : 'those days'}.`
                            : `${part === 'summary' ? docFileName(cells, jobKey).replace(/\.pdf$/i, ' Week Summary.pdf') : docFileName(cells, jobKey)} — ${PART_BLURB[part]}`}
                    </Text>
                    {pEnv ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                            <Text style={{ fontSize: 11, color: '#64748b' }}>Envelope:</Text>
                            {renderEnvelope(pEnv)}
                        </View>
                    ) : null}
                    {pTestEnv ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                            <Text style={{ fontSize: 11, color: '#64748b' }}>Latest test envelope:</Text>
                            {renderEnvelope(pTestEnv)}
                        </View>
                    ) : null}
                    {testFlow ? null : to.length ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                            <Text style={{ fontSize: 11, color: '#64748b', marginRight: 6 }}>Goes to:</Text>
                            {to.map((r) => {
                                const tick = !offSigners[offKey(part, r)];
                                return (
                                    <Pressable
                                        key={r.email}
                                        onPress={() => !pLocked && toggleSigner(part, r)}
                                        disabled={pLocked}
                                        title={tick ? `${r.email} — click to leave off this send` : `${r.email} — click to put back on`}
                                        style={{ flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: tick ? '#cbd5e1' : '#e2e8f0', borderRadius: 999, paddingTop: 2, paddingBottom: 2, paddingLeft: 7, paddingRight: 9, marginRight: 4, marginBottom: 2, backgroundColor: tick ? '#fff' : '#f8fafc', opacity: tick ? 1 : 0.55, cursor: pLocked ? 'default' : 'pointer' }}
                                    >
                                        <View style={{ width: 11, height: 11, borderRadius: 3, borderWidth: 1.5, borderStyle: 'solid', borderColor: tick ? '#0f172a' : '#94a3b8', backgroundColor: tick ? '#0f172a' : '#fff', alignItems: 'center', justifyContent: 'center', marginRight: 5 }}>
                                            {tick ? <Text style={{ color: '#fff', fontSize: 7, lineHeight: 1 }}>✓</Text> : null}
                                        </View>
                                        <Text style={{ fontSize: 10, color: tick ? '#0f172a' : '#64748b', fontWeight: '600', textDecoration: tick ? 'none' : 'line-through' }}>
                                            {`${r.name || r.email} · ${r.role === 'All' ? 'signs all' : `${r.role} tickets`}`}
                                        </Text>
                                    </Pressable>
                                );
                            })}
                            {!on.length ? (
                                <Text style={{ fontSize: 10, color: '#b45309', fontWeight: '600', marginLeft: 4 }}>Nobody ticked — nothing to send.</Text>
                            ) : null}
                        </View>
                    ) : (
                        <Text style={{ fontSize: 11, color: '#b45309', marginBottom: 6 }}>
                            No signer map on this job yet — set who signs what under Rates &amp; Rules › Markup &amp; settings. Until then the single signer below is used.
                        </Text>
                    )}
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                        <View style={{ marginRight: 8 }}>
                            <OutlineButton label={mine('preview') ? 'Building…' : 'Preview'} onPress={() => makePacket(false, part)} disabled={!cells.length || Boolean(busy) || noPeople} />
                        </View>
                        <View style={{ marginRight: 8 }}>
                            <OutlineButton label={mine('download') ? 'Building…' : 'Download PDF'} onPress={() => makePacket(true, part)} disabled={!cells.length || Boolean(busy) || noPeople} />
                        </View>
                        {recordRecon && !testFlow && !isSummaryDoc(doc) ? (
                            <View style={{ marginRight: 8 }}>
                                <OutlineButton label={mine('recon') ? 'Recording…' : 'Record HJ snapshot'} onPress={() => recordSnapshot(part)} disabled={!cells.length || Boolean(busy) || noPeople} />
                            </View>
                        ) : null}
                        {testFlow && !versionLocked && !formHere ? (
                            <Pressable onPress={() => openForm(part)} disabled={!testTo.length} style={btnDark(!testTo.length)}>
                                <Text style={{ fontSize: 11, color: '#fff', fontWeight: '700' }}>
                                    {testTo[0]?.email ? `Test send to ${testTo[0].email}` : 'Test send — no address on your login'}
                                </Text>
                            </Pressable>
                        ) : !testFlow && !pLocked && !formHere ? (
                            <Pressable onPress={() => openForm(part)} disabled={off} style={btnDark(off)}>
                                <Text style={{ fontSize: 11, color: '#fff', fontWeight: '700' }}>Send for Signature</Text>
                            </Pressable>
                        ) : null}
                    </View>
                    {busy && String(busy).endsWith(`:${part}`) ? renderBusy() : null}
                    {formHere && (testFlow ? !versionLocked : !pLocked) ? renderForm(part, on) : null}
                </View>
            );
        };

        return (
            <View>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <View style={{ width: 22, height: 22, borderRadius: 999, backgroundColor: c, alignItems: 'center', justifyContent: 'center', marginRight: 8 }}>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff' }}>{letterOf(doc)}</Text>
                    </View>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: '#0f172a' }}>{docName(doc.cells)}</Text>
                    {isSummaryDoc(doc) ? <Text style={{ fontSize: 11, color: '#475569', fontWeight: '700', marginLeft: 8 }}>WEEK SUMMARY</Text> : null}
                    {isSupplementDoc(doc) ? <Text style={{ fontSize: 11, color: '#475569', fontWeight: '700', marginLeft: 8 }}>SUPPLEMENT</Text> : null}
                    <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 10 }}>{stageLabel(doc)}</Text>
                    <View style={{ flexGrow: 1 }} />
                    {!locked ? (
                        <Pressable onPress={removeDoc} style={{ cursor: 'pointer' }}><Text style={{ fontSize: 11, color: '#991b1b', fontWeight: '600' }}>{isSummaryDoc(doc) ? 'Remove week summary' : isSupplementDoc(doc) ? 'Remove supplement' : 'Remove ticket'}</Text></Pressable>
                    ) : null}
                </View>
                {/* Each send shows its own envelope under its own heading;
                    up here only the fact that one of them is out, which is
                    what freezes the whole ticket. */}
                {locked && anyEnvLive ? (
                    <Text style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                        An envelope is out, so this ticket is frozen. Void it to change days, backup or hours.
                    </Text>
                ) : null}
                {doc.signed ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                        <Text style={{ fontSize: 11, color: '#166534', fontWeight: '600', marginRight: 8 }}>
                            {`Signed copy uploaded ${doc.signed.t || ''} by ${doc.signed.u || '—'}`}
                        </Text>
                        {doc.signed.url ? (
                            <Pressable onPress={() => window.open(doc.signed.url, '_blank', 'noopener,noreferrer')} style={{ cursor: 'pointer' }}>
                                <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{doc.signed.name || 'Open'}</Text>
                            </Pressable>
                        ) : <Text style={{ fontSize: 11, color: '#64748b' }}>{doc.signed.name}</Text>}
                    </View>
                ) : null}

                {step(1, 'Covers', !locked ? (
                    <Pressable onPress={() => setEditCells((e) => !e)} style={{ cursor: 'pointer' }}>
                        <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{editCells ? 'Done picking days' : 'Pick days on the map'}</Text>
                    </Pressable>
                ) : null)}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {doc.cells.map((k) => {
                        const { d, sh } = splitCell(k);
                        return (
                            <View key={k} style={{ borderWidth: 1, borderColor: c, borderRadius: 4, paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2, marginRight: 4, marginBottom: 4, backgroundColor: `${c}14` }}>
                                <Text style={{ fontSize: 11, color: '#0f172a' }}>{`${md(d)} ${sh}`}</Text>
                            </View>
                        );
                    })}
                    {!doc.cells.length ? <Text style={{ fontSize: 12, color: '#b45309' }}>No days yet — click cells on the map.</Text> : null}
                </View>
                {editCells && !locked ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
                        <Text style={{ fontSize: 11, color: '#64748b', marginRight: 10 }}>Click cells on the map to add or remove them — or add a day the list doesn&apos;t show:</Text>
                        {createElement('input', {
                            type: 'date',
                            value: extraDate,
                            onChange: (e) => setExtraDate(e.target.value || ''),
                            style: { fontSize: 11, padding: 3, borderRadius: 4, border: '1px solid #cbd5e1', marginRight: 6 },
                        })}
                        {DOC_SHIFTS.map((sh) => (
                            <Pressable
                                key={sh}
                                onPress={() => {
                                    if (!/^\d{4}-\d{2}-\d{2}$/.test(extraDate)) { showToast('Pick a date first.', 'warn'); return; }
                                    const k = cellKey(extraDate, sh);
                                    if (doc.cells.includes(k)) return;
                                    const owner = cover[k];
                                    if (owner && !sharesCells(doc)) { showToast(`${md(extraDate)} ${sh} is on ticket ${letterOf(owner)} — ${docName(owner.cells)}. For someone that ticket missed, start a supplement instead.`, 'warn'); return; }
                                    persist({ ...doc, cells: sortCells([...doc.cells, k]) });
                                }}
                                style={{ borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 4, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8, marginRight: 4, cursor: 'pointer' }}
                            >
                                <Text style={{ fontSize: 11, color: '#0f172a', fontWeight: '600' }}>{`+ ${sh}`}</Text>
                            </Pressable>
                        ))}
                    </View>
                ) : null}

                {isSupplementDoc(doc) ? (() => {
                    const picked = doc.only || [];
                    const pickedIds = new Set(picked.map((x) => String(x.id)));
                    const known = new Set(crew.people.map((x) => x.id));
                    // Picked earlier, no hours on these days now (a day taken off,
                    // or the feed changed): still listed so it can be unticked.
                    const gone = picked.filter((x) => !known.has(String(x.id)));
                    const person = (pr, on, hours) => (
                        <Pressable key={pr.id} onPress={() => togglePerson(pr)} disabled={locked} style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 2, paddingBottom: 2, cursor: locked ? 'default' : 'pointer' }}>
                            <View style={{ width: 13, height: 13, borderRadius: 3, borderWidth: 1.5, borderStyle: 'solid', borderColor: on ? '#0f172a' : '#94a3b8', backgroundColor: on ? '#0f172a' : '#fff', alignItems: 'center', justifyContent: 'center', marginRight: 7 }}>
                                {on ? <Text style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</Text> : null}
                            </View>
                            <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: on ? '600' : '400' }}>{pr.name || pr.id}</Text>
                            {pr.cls ? <Text style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>{pr.cls}</Text> : null}
                            <Text style={{ fontSize: 10, color: hours ? '#64748b' : '#b45309', marginLeft: 8 }}>{hours || 'no hours on these days now'}</Text>
                        </Pressable>
                    );
                    const hoursOf = (pr) => Object.entries(pr.hours || {}).map(([k, h]) => { const { d, sh } = splitCell(k); return `${md(d)} ${sh} ${Math.round(h * 4) / 4}h`; }).join(' · ');
                    return (
                        <View style={{ marginTop: 8 }}>
                            <Text style={{ ...sectionLabel, marginBottom: 2 }}>WHO IS ON IT — only these people print</Text>
                            <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                                Tick the people the signed ticket missed. Their hours come from the same sources as any ticket (step 3); nobody else and no equipment goes on this document.
                            </Text>
                            {!doc.cells.length ? <Text style={{ fontSize: 12, color: '#b45309' }}>Put the day on the supplement first — the people with hours that day list here.</Text> : null}
                            {crew.error ? <Text style={{ fontSize: 12, color: '#991b1b' }}>{`Could not read the day's people — ${crew.error}`}</Text> : null}
                            {crew.people.map((pr) => person(pr, pickedIds.has(pr.id), hoursOf(pr)))}
                            {gone.map((pr) => person(pr, true, ''))}
                            {crew.loading ? <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{`Reading hours for ${docName(doc.cells)}…`}</Text> : null}
                            {!crew.loading && doc.cells.length && !crew.people.length && !crew.error ? <Text style={{ fontSize: 12, color: '#b45309' }}>Nobody has hours on these days yet. If the person is not in HeavyJob or HEET, they cannot be ticketed from here.</Text> : null}
                            {noPeople && crew.people.length ? <Text style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>Nobody picked yet — the packet stays empty until someone is ticked.</Text> : null}
                        </View>
                    );
                })() : null}

                {step(2, 'Backup from SharePoint', !locked && uploadFile ? (
                    <>
                        {createElement('input', { ref: backupRef, type: 'file', multiple: true, accept: DOC_ACCEPT, style: { display: 'none' }, onChange: pickBackup })}
                        <Pressable onPress={() => !uploading && backupRef.current && backupRef.current.click()} style={{ cursor: uploading ? 'default' : 'pointer' }} title={firstDate ? `Files into 02.06 - Timecards under ${md(firstDate)} and goes on this ticket as backup` : 'Put a day on the ticket first'}>
                            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{uploading === 'backup' ? 'Uploading…' : 'Upload backup from this computer'}</Text>
                        </Pressable>
                    </>
                ) : null)}
                {renderFiles()}

                {renderSources()}

                {/* Everything the client will get, in one PDF: read the
                    whole thing here before the three sends below go out
                    separately. */}
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                    <View style={{ marginRight: 8 }}>
                        <OutlineButton label={busy === 'preview:all' ? 'Building…' : 'Preview packet'} onPress={previewPacket} disabled={!doc.cells.length || Boolean(busy) || noPeople} />
                    </View>
                    <View style={{ marginRight: 8 }}>
                        <OutlineButton label={busy === 'download:all' ? 'Building…' : 'Download PDF'} onPress={downloadPacket} disabled={!doc.cells.length || Boolean(busy) || noPeople} />
                    </View>
                    {busy && String(busy).endsWith(':all') ? renderBusy() : null}
                </View>

                {/* What has been built off this ticket so far — every preview,
                    download and send, as filed in the archive. */}
                <View style={{ marginTop: 12 }}>
                    <Text style={{ ...sectionLabel, marginBottom: 3 }}>PACKETS BUILT FOR THIS TICKET</Text>
                    {renderPackets(packets.list.filter((p) => p.docId === doc.id))}
                </View>


                {/* ── Sending ──
                    Three documents go to the client, not one: the day crew's
                    tickets, the night crew's, and the week summary over both.
                    Each is its own envelope, so the day rep signs their week
                    without waiting on the night rep. Everything they share —
                    the flow, the gates, the equipment basis — is stated once
                    here, above the three sections. */}
                <View style={{ marginTop: 16, borderTopWidth: 1, borderColor: '#f1f5f9', paddingTop: 8 }}>
                    <Text style={{ ...sectionLabel, marginBottom: 4 }}>SENDING</Text>
                </View>
                {setSigFlow ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                        <Text style={{ fontSize: 11, color: '#64748b', marginRight: 6 }}>Flow:</Text>
                        {[['client', 'Client signers'], ['test', 'Test — only me']].map(([v, label]) => (
                            <Pressable
                                key={v}
                                onPress={() => { setSigFlow(v); setForm(null); }}
                                style={{ borderWidth: 1, borderColor: sigFlow === v ? '#0f172a' : '#cbd5e1', backgroundColor: sigFlow === v ? '#0f172a' : '#fff', borderRadius: 999, paddingTop: 2, paddingBottom: 2, paddingLeft: 9, paddingRight: 9, marginRight: 4, cursor: 'pointer' }}
                            >
                                <Text style={{ fontSize: 10, fontWeight: '600', color: sigFlow === v ? '#fff' : '#334155' }}>{label}</Text>
                            </Pressable>
                        ))}
                        {docusignDemo ? <Text style={{ fontSize: 10, color: '#b45309', fontWeight: '700', marginLeft: 6 }}>DocuSign DEMO</Text> : null}
                    </View>
                ) : null}
                {testFlow ? (
                    <View style={{ borderLeftWidth: 3, borderColor: '#b45309', backgroundColor: '#fffbeb', paddingTop: 5, paddingBottom: 5, paddingLeft: 9, paddingRight: 9, marginBottom: 6 }}>
                        <Text style={{ fontSize: 11, color: '#78350f' }}>
                            {testTo[0]?.email
                                ? `TEST: whichever send you press goes to ${testTo[0].email} only, holding every signature line. Check and approval are skipped, the ticket's status is untouched, and the signed copy is not filed.`
                                : 'TEST: a test send goes to whoever is signed in — but this session has no email address on your login, so there is nowhere to send it. Sign out and back in, or use Client signers.'}
                        </Text>
                    </View>
                ) : null}
                {/* Which equipment the packet will bill, and the one control
                    that changes it. Labor is always the HeavyJob feed. */}
                {doc.cells.length && importTimecard ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                        <Text style={{ fontSize: 11, color: weeksOnWorkbook.length === docWeeks.length ? '#047857' : '#b45309', fontWeight: '600', marginRight: 8 }}>
                            {weeksOnWorkbook.length === docWeeks.length
                                ? `Equipment: reviewed time card (${docWeeks.map(md).join(', ')})`
                                : 'Equipment: HeavyJob hours — no reviewed time card for this week yet'}
                        </Text>
                        {!versionLocked ? (
                            <>
                                {createElement('input', {
                                    ref: timecardRef,
                                    type: 'file',
                                    accept: '.xlsx,.xlsm',
                                    style: { display: 'none' },
                                    onChange: pickTimecard,
                                })}
                                <Pressable
                                    onPress={() => !uploading && timecardRef.current && timecardRef.current.click()}
                                    style={{ cursor: uploading ? 'default' : 'pointer' }}
                                    title="The reviewed weekly time card workbook (Labor + Equipment tabs). Its Equipment tab becomes the billed equipment for that week."
                                >
                                    <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>
                                        {uploading === 'timecard' ? 'Reading…' : weeksOnWorkbook.length === docWeeks.length ? 'Replace time card' : 'Load reviewed time card'}
                                    </Text>
                                </Pressable>
                            </>
                        ) : null}
                    </View>
                ) : null}

                {parts.map((part, i) => renderSend(part, 4 + i))}

                {/* Paper route: the client signed a printed ticket, so the
                    scan IS the signed copy for the whole ticket — no
                    envelopes. Only while none of the three is live. */}
                {!versionLocked && uploadFile && doc.st !== 'Signed' && !anyEnvLive ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
                        {createElement('input', { ref: signedRef, type: 'file', accept: DOC_ACCEPT, style: { display: 'none' }, onChange: pickSigned })}
                        <Pressable onPress={() => !uploading && signedRef.current && signedRef.current.click()} style={{ cursor: uploading ? 'default' : 'pointer' }} title="The client signed on paper — file the scan as this ticket's signed copy and mark it Signed">
                            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{uploading === 'signed' ? 'Uploading…' : 'Upload signed copy instead — all three'}</Text>
                        </Pressable>
                    </View>
                ) : null}

                <View style={{ marginTop: 16, borderTopWidth: 1, borderColor: '#f1f5f9', paddingTop: 8 }}>
                    <Text style={{ ...sectionLabel, marginBottom: 3 }}>HISTORY</Text>
                    {doc.h.map((e, j) => (
                        <Text key={j} style={{ fontSize: 11, color: '#64748b', paddingTop: 1 }}>{`${e.t}  ${e.s} — ${e.u}${e.n ? `: ${e.n}` : ''}`}</Text>
                    ))}
                    {/* All three envelopes' events, in one run — each line
                        says which send it came from. */}
                    {[...new Map(parts.filter((p2) => envs[p2]).map((p2) => [envs[p2].RowID, p2])).values()].flatMap((part) => (envs[part]?.events || []).map((v) => (
                        <Text key={`${part}-${v.RowID}`} style={{ fontSize: 11, color: '#64748b', paddingTop: 1 }}>
                            {`${v.Occurred_At_Utc ? moment.utc(v.Occurred_At_Utc).local().format('YYYY-MM-DD HH:mm') : ''}  DocuSign ${v.Event} (${part}) — ${v.Actor || 'system'}${v.Note ? `: ${v.Note}` : ''}`}
                        </Text>
                    )))}
                </View>
            </View>
        );
    };

    return (
        <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,23,42,0.45)', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
            <View style={{ backgroundColor: '#f8fafc', borderRadius: 8, paddingTop: 14, paddingBottom: 14, paddingLeft: 16, paddingRight: 16, width: '96%', maxWidth: 1240, height: '92%', flexDirection: 'column' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: '#0f172a' }}>{`Ticket Map — Job ${jobKey}`}</Text>
                    <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 10 }}>Which signed document covers each work day and shift</Text>
                    <View style={{ flexGrow: 1 }} />
                    {doc ? (
                        <Pressable onPress={() => { setForm(null); setSelId(null); setEditCells(false); }} title="Every week: daily tickets, week summary and invoice" style={{ marginRight: 12, cursor: 'pointer' }}>
                            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>Week tracker</Text>
                        </Pressable>
                    ) : null}
                    {!versionLocked ? (
                        <>
                            <Pressable onPress={() => { setForm(null); startNew('supplement'); }} title="A ticket for people a signed ticket missed — it can sit on days another ticket already covers" style={{ marginRight: 12, cursor: 'pointer' }}>
                                <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>+ Supplement (someone missed)</Text>
                            </Pressable>
                            <Pressable onPress={() => { setForm(null); startNew(); }} style={{ ...btnDark(false), marginRight: 8 }}>
                                <Text style={{ fontSize: 11, color: '#fff', fontWeight: '700' }}>+ New field ticket</Text>
                            </Pressable>
                        </>
                    ) : null}
                    <Pressable onPress={onClose} style={{ paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10, cursor: 'pointer' }}>
                        <Text style={{ fontSize: 13, color: '#475569', fontWeight: '700' }}>Close</Text>
                    </Pressable>
                </View>
                <View style={{ flexDirection: 'row', flexGrow: 1, minHeight: 0 }}>
                    {renderMap()}
                    <View style={{ ...card, flexGrow: 1, flexShrink: 1, minWidth: 0, marginLeft: 12, paddingTop: 12, paddingBottom: 16, paddingLeft: 16, paddingRight: 16, overflow: 'auto' }}>
                        {renderDocList()}
                        {renderDoc()}
                    </View>
                </View>
            </View>
        </View>
    );
};

export default TicketMap;
